/* =====================================================================
 * MÔDERN MATCH™ — Shop For Your Space
 * Môdern Studio™ unified front door.
 *
 * One natural-language input replaces the AI Gift Finder and the AI
 * Product Finder. It parses what the shopper typed into a structured
 * intent, then routes to one of four engines:
 *
 *   room  — a coordinated, budget-allocated set for a whole room
 *   item  — ranked matches for one specific piece
 *   look  — a style board across categories when only a style is given
 *   gift  — gift-shaped results with a recipient and an occasion
 *
 * Every pick carries the reasons it was chosen, and every pick can be
 * swapped for the next best candidate without rebuilding the plan.
 *
 * DATA NOTE: the room slot tables below are copied from
 * studio-budget-planner.js so that both tools allocate a budget the
 * same way. They are duplicated deliberately for now — pulling them
 * into a shared asset means touching the working Budget Planner, which
 * is a separate change worth making on its own.
 *
 * All pure logic hangs off window.MSGMatch so it can be tested in Node
 * without a DOM.
 * ================================================================== */
(function () {
  'use strict';

  /* ===================================================================
   * 1. VOCABULARY
   * ================================================================ */

  /* Rooms map to a real collection handle. dining-room and kitchen share
   * the kitchen collection but ask for different slots. */
  var ROOMS = {
    'living-room': {
      label: 'Living Room', collection: 'living-room-collection',
      aliases: ['living room', 'livingroom', 'living area', 'family room', 'great room',
                'sitting room', 'lounge', 'tv room', 'den']
    },
    'bedroom': {
      label: 'Bedroom', collection: 'bedroom-collection',
      aliases: ['primary bedroom', 'master bedroom', 'guest bedroom', 'guest room',
                'spare room', 'bedroom', 'bed room', 'nursery']
    },
    'dining-room': {
      label: 'Dining Room', collection: 'kitchen-collection',
      aliases: ['dining room', 'dining area', 'dining nook', 'breakfast nook',
                'eat in kitchen', 'dining space', 'dining']
    },
    'kitchen': {
      label: 'Kitchen', collection: 'kitchen-collection',
      aliases: ['kitchen', 'pantry', 'coffee bar', 'coffee station']
    },
    'office': {
      label: 'Home Office', collection: 'office-furniture',
      aliases: ['home office', 'office', 'study', 'workspace', 'work space',
                'desk setup', 'work from home', 'wfh']
    },
    'bathroom': {
      label: 'Bathroom', collection: 'bathroom-collection',
      aliases: ['primary bathroom', 'master bathroom', 'powder room', 'half bath',
                'guest bathroom', 'bathroom', 'ensuite', 'bath room']
    },
    'outdoor': {
      label: 'Outdoor', collection: 'outdoor-furniture',
      aliases: ['patio', 'back patio', 'front porch', 'screened porch', 'porch',
                'deck', 'balcony', 'backyard', 'back yard', 'terrace', 'outdoor space',
                'outdoors', 'outdoor', 'garden']
    },
    'entryway': {
      label: 'Entryway', collection: 'home-decor-collection',
      aliases: ['entryway', 'entry way', 'entry', 'entrance', 'foyer', 'hallway',
                'hall way', 'mudroom', 'mud room']
    }
  };

  /* Product types, verbatim from the store taxonomy (156 real values).
   * `pools` are the collections searched when no room was named. */
  var CATEGORIES = {
    'sofa':            { label: 'Sofa', types: ['sofas', 'loveseats', 'daybeds & futons'],
                         aliases: ['sofa', 'couch', 'settee'], pools: ['living-room-collection'] },
    'sectional':       { label: 'Sectional', types: ['sectional sofas'],
                         aliases: ['sectional', 'l shaped couch', 'l shaped sofa', 'corner sofa'], pools: ['living-room-collection'] },
    'loveseat':        { label: 'Loveseat', types: ['loveseats'],
                         aliases: ['loveseat', 'love seat', 'two seater', '2 seater'], pools: ['living-room-collection'] },
    'accent-chair':    { label: 'Accent Chair', types: ['accent chairs', 'recliners & armchairs', 'chaise lounges'],
                         aliases: ['accent chair', 'armchair', 'arm chair', 'lounge chair', 'reading chair',
                                   'occasional chair', 'club chair', 'recliner', 'chaise', 'chair'], pools: ['living-room-collection'] },
    'ottoman':         { label: 'Ottoman', types: ['ottomans & poufs'],
                         aliases: ['ottoman', 'pouf', 'pouffe', 'footstool', 'foot stool'], pools: ['living-room-collection'] },
    'coffee-table':    { label: 'Coffee Table', types: ['coffee tables'],
                         aliases: ['coffee table', 'cocktail table', 'centre table', 'center table'], pools: ['living-room-collection'] },
    'side-table':      { label: 'Side Table', types: ['side & end tables'],
                         aliases: ['side table', 'end table', 'accent table', 'nesting table'], pools: ['living-room-collection'] },
    'console-table':   { label: 'Console Table', types: ['console tables'],
                         aliases: ['console table', 'console', 'sofa table', 'entry table', 'entryway table'], pools: ['living-room-collection', 'home-decor-collection'] },
    'tv-stand':        { label: 'TV Stand', types: ['tv stands & media consoles'],
                         aliases: ['tv stand', 'media console', 'tv console', 'entertainment center', 'media unit'], pools: ['living-room-collection'] },
    'rug':             { label: 'Rug', types: ['rugs'],
                         aliases: ['area rug', 'rug', 'runner', 'carpet'], pools: ['living-room-collection', 'bedroom-collection'] },
    'throw-pillow':    { label: 'Throw Pillows', types: ['throw pillows', 'pillow covers'],
                         aliases: ['throw pillow', 'throw pillows', 'cushion', 'cushions', 'pillow cover', 'accent pillow'], pools: ['living-room-collection', 'bedroom-collection'] },
    'curtains':        { label: 'Curtains', types: ['curtains & drapes'],
                         aliases: ['curtain', 'curtains', 'drapes', 'drapery', 'window treatment'], pools: ['home-decor-collection', 'living-room-collection'] },
    'floor-lamp':      { label: 'Floor Lamp', types: ['table & floor lamps'],
                         aliases: ['floor lamp', 'arc lamp', 'arch lamp', 'torchiere', 'tree lamp', 'standing lamp'],
                         titleAny: ['floor lamp', 'arc lamp', 'arch lamp', 'torchiere', 'tree lamp'], pools: ['living-room-collection'] },
    'table-lamp':      { label: 'Table Lamp', types: ['table & floor lamps'],
                         aliases: ['table lamp', 'bedside lamp', 'desk lamp', 'buffet lamp', 'nightstand lamp'],
                         titleAny: ['table lamp', 'desk lamp', 'bedside lamp', 'book lamp', 'buffet lamp'], pools: ['living-room-collection', 'bedroom-collection'] },
    'pendant':         { label: 'Ceiling & Pendant Light', types: ['ceiling & pendant lighting'],
                         aliases: ['pendant light', 'pendant lighting', 'chandelier', 'ceiling light',
                                   'flush mount', 'hanging light'], pools: ['living-room-collection', 'home-decor-collection'] },
    'wall-art':        { label: 'Wall Art', types: ['wall art', 'wall decor', 'acoustic & wall panels'],
                         aliases: ['wall art', 'canvas art', 'framed art', 'art print', 'wall decor',
                                   'artwork', 'prints', 'gallery wall'], pools: ['home-decor-collection', 'wall-art', 'living-room-collection'] },
    'mirror':          { label: 'Mirror', types: ['mirrors'],
                         aliases: ['floor mirror', 'full length mirror', 'leaning mirror', 'arch mirror',
                                   'wall mirror', 'mirror'], pools: ['home-decor-collection', 'bedroom-collection'] },
    'plant':           { label: 'Faux Plants', types: ['faux plants & trees', 'planters & plant stands'],
                         aliases: ['faux plant', 'artificial plant', 'fake plant', 'faux tree',
                                   'artificial tree', 'planter', 'plant stand', 'greenery'], pools: ['home-decor-collection', 'living-room-collection'] },
    'vase':            { label: 'Vase', types: ['vases', 'sculptures & objects', 'decorative trays'],
                         aliases: ['vase', 'sculpture', 'decor object', 'centerpiece', 'decorative tray',
                                   'coffee table decor', 'shelf decor'], pools: ['home-decor-collection'] },
    'candle':          { label: 'Candles & Fragrance', types: ['candles & holders', 'home fragrance'],
                         aliases: ['candle', 'candles', 'candle holder', 'diffuser', 'reed diffuser', 'home fragrance'], pools: ['home-decor-collection', 'bathroom-collection'] },
    'clock':           { label: 'Clock', types: ['clocks'],
                         aliases: ['wall clock', 'clock'], pools: ['home-decor-collection'] },
    'bookcase':        { label: 'Bookcase & Shelving', types: ['bookcases & shelving'],
                         aliases: ['bookcase', 'bookshelf', 'book shelf', 'shelving', 'shelving unit',
                                   'etagere', 'open shelving', 'ladder shelf'], pools: ['office-furniture', 'living-room-collection'] },
    'cabinet':         { label: 'Sideboard & Cabinet', types: ['cabinets & sideboards', 'storage chests'],
                         aliases: ['sideboard', 'buffet', 'credenza', 'cabinet', 'storage chest', 'accent cabinet'], pools: ['living-room-collection', 'bedroom-collection'] },
    'bench':           { label: 'Bench', types: ['benches'],
                         aliases: ['bench', 'entry bench', 'storage bench', 'end of bed bench'], pools: ['home-decor-collection', 'living-room-collection', 'bedroom-collection'] },
    'basket':          { label: 'Baskets & Bins', types: ['baskets & bins', 'storage boxes', 'wall organizers'],
                         aliases: ['basket', 'baskets', 'storage bin', 'storage basket', 'woven basket', 'organizer'], pools: ['home-decor-collection', 'bathroom-collection'] },
    'electric-fire':   { label: 'Electric Fireplace', types: ['electric fireplaces'],
                         aliases: ['electric fireplace', 'fireplace', 'fireplace insert', 'stove heater'], pools: ['living-room-collection'] },

    'bed':             { label: 'Bed Frame', types: ['beds & bed frames', 'headboards'],
                         aliases: ['bed frame', 'platform bed', 'bed', 'headboard', 'upholstered bed',
                                   'canopy bed', 'storage bed'], pools: ['bedroom-collection'] },
    'mattress':        { label: 'Mattress', types: ['mattresses', 'air mattresses'],
                         aliases: ['mattress', 'memory foam mattress', 'hybrid mattress'], pools: ['bedroom-collection'] },
    'nightstand':      { label: 'Nightstand', types: ['nightstands'],
                         aliases: ['nightstand', 'night stand', 'bedside table', 'bed side table'], pools: ['bedroom-collection'] },
    'dresser':         { label: 'Dresser', types: ['dressers'],
                         aliases: ['dresser', 'chest of drawers', 'drawer chest', 'tallboy'], pools: ['bedroom-collection', 'bedroom-dressers'] },
    'bedding':         { label: 'Bedding', types: ['bedding sets', 'sheets & sheet sets'],
                         aliases: ['bedding', 'comforter', 'duvet', 'duvet cover', 'quilt', 'sheet set',
                                   'sheets', 'bed set', 'coverlet'], pools: ['bedroom-collection'] },

    'desk':            { label: 'Desk', types: ['desks'],
                         aliases: ['desk', 'writing desk', 'standing desk', 'computer desk', 'work desk'], pools: ['office-furniture', 'modern-office-desk'] },
    'office-chair':    { label: 'Office Chair', types: ['office chairs'],
                         aliases: ['office chair', 'desk chair', 'task chair', 'ergonomic chair', 'computer chair'], pools: ['office-furniture', 'office-chairs'] },

    'dining-table':    { label: 'Dining Table', types: ['dining tables', 'dining sets'],
                         aliases: ['dining table', 'kitchen table', 'dinner table', 'bistro table', 'dining set'], pools: ['kitchen-collection'] },
    'dining-chair':    { label: 'Dining Chairs', types: ['dining chairs'],
                         aliases: ['dining chair', 'dining chairs', 'kitchen chair', 'side chair'], pools: ['kitchen-collection'] },
    'bar-stool':       { label: 'Bar Stools', types: ['stools & counter stools'],
                         aliases: ['bar stool', 'bar stools', 'counter stool', 'counter stools', 'stool'], pools: ['kitchen-collection'] },
    'dinnerware':      { label: 'Dinnerware', types: ['dinnerware', 'bowls', 'flatware', 'table linens'],
                         aliases: ['dinnerware', 'dish set', 'dishes', 'plates', 'flatware', 'silverware',
                                   'cutlery', 'place setting'], pools: ['kitchen-collection'] },
    'glassware':       { label: 'Glassware', types: ['wine glasses & stemware', 'drinking glasses', 'mugs', 'drinkware', 'tumblers'],
                         aliases: ['wine glass', 'wine glasses', 'stemware', 'glassware', 'drinking glasses',
                                   'tumbler', 'mug', 'mugs'], pools: ['kitchen-collection'] },
    'cookware':        { label: 'Cookware', types: ['cookware', 'knives & knife storage', 'cutting boards'],
                         aliases: ['cookware', 'pots and pans', 'pot and pan', 'frying pan', 'skillet',
                                   'dutch oven', 'knife set', 'knives', 'cutting board'], pools: ['kitchen-collection'] },
    'coffee-maker':    { label: 'Coffee & Espresso', types: ['coffee & espresso machines', 'electric kettles', 'teapots & coffee servers', 'tea makers', 'coffee accessories'],
                         aliases: ['espresso machine', 'coffee maker', 'coffee machine', 'french press',
                                   'electric kettle', 'kettle', 'teapot'], pools: ['kitchen-collection'] },
    'small-appliance': { label: 'Small Appliance', types: ['air fryers', 'blenders & mixers', 'bread makers', 'toasters & toaster ovens',
                                                           'juicers', 'slow cookers', 'pressure cookers', 'ice cream makers', 'food dehydrators',
                                                           'specialty kitchen appliances', 'pasta makers', 'sous vide', 'deep fryers', 'yogurt makers',
                                                           'indoor grills & griddles'],
                         aliases: ['air fryer', 'blender', 'stand mixer', 'mixer', 'bread maker', 'breadmaker',
                                   'toaster', 'toaster oven', 'juicer', 'slow cooker', 'crock pot', 'pressure cooker',
                                   'instant pot', 'ice cream maker', 'dehydrator', 'sous vide', 'griddle'], pools: ['kitchen-collection', 'kitchen-countertop-appliances'] },
    'fridge':          { label: 'Refrigeration', types: ['refrigerators', 'mini fridges', 'chest freezers', 'beverage & wine coolers', 'ice makers'],
                         aliases: ['refrigerator', 'fridge', 'mini fridge', 'freezer', 'wine cooler',
                                   'beverage cooler', 'ice maker'], pools: ['kitchen-collection'] },
    'kitchen-storage': { label: 'Kitchen Storage', types: ['kitchen canisters', 'food storage containers', 'kitchen organizers'],
                         aliases: ['canister', 'canisters', 'food storage', 'storage container', 'pantry organizer',
                                   'kitchen organizer'], pools: ['kitchen-collection', 'organization-decor'] },

    'bath-towel':      { label: 'Bath Towels', types: ['bath towels'],
                         aliases: ['bath towel', 'bath towels', 'towel set', 'towels', 'hand towel'], pools: ['bathroom-collection', 'bathroom-towels'] },
    'bath-mat':        { label: 'Bath Mat', types: ['bath mats'],
                         aliases: ['bath mat', 'bath rug', 'bathroom rug', 'bath mats'], pools: ['bathroom-collection', 'bathroom-rugs'] },
    'shower-curtain':  { label: 'Shower Curtain', types: ['shower curtains'],
                         aliases: ['shower curtain', 'shower curtains'], pools: ['bathroom-collection'] },
    'bath-accessory':  { label: 'Bath Accessories', types: ['bath accessories', 'soap dispensers', 'towel bars & hooks', 'bath storage'],
                         aliases: ['bath accessories', 'soap dispenser', 'towel bar', 'towel hook',
                                   'bath storage', 'vanity tray', 'bathroom set'], pools: ['bathroom-collection', 'bathroom-essentials'] },

    'patio-set':       { label: 'Patio Set', types: ['patio furniture sets', 'patio dining sets'],
                         aliases: ['patio set', 'patio furniture set', 'outdoor set', 'conversation set',
                                   'patio dining set', 'outdoor dining set'], pools: ['outdoor-furniture', 'outdoor-furniture-sets'] },
    'patio-seating':   { label: 'Patio Seating', types: ['patio seating', 'patio benches', 'patio ottomans'],
                         aliases: ['patio chair', 'patio seating', 'outdoor chair', 'outdoor seating',
                                   'patio bench', 'outdoor bench', 'adirondack'], pools: ['outdoor-furniture'] },
    'patio-table':     { label: 'Patio Table', types: ['patio tables'],
                         aliases: ['patio table', 'outdoor table', 'bistro set'], pools: ['outdoor-furniture'] },
    'fire-pit':        { label: 'Fire Pit', types: ['fire pits'],
                         aliases: ['fire pit', 'firepit', 'fire table', 'fire bowl'], pools: ['outdoor-furniture'] },
    'grill':           { label: 'Grill', types: ['grills', 'food smokers'],
                         aliases: ['grill', 'bbq', 'barbecue', 'smoker', 'pellet grill', 'griller'], pools: ['outdoor-furniture', 'outdoor-grills'] },
    'water-feature':   { label: 'Water Feature', types: ['water features'],
                         aliases: ['water fountain', 'fountain', 'water feature'], pools: ['outdoor-furniture', 'indoor-outdoor-waterfountains'] },
    'gazebo':          { label: 'Gazebo & Canopy', types: ['gazebos & canopies'],
                         aliases: ['gazebo', 'canopy', 'pergola', 'shade sail'], pools: ['outdoor-furniture'] },
    'hot-tub':         { label: 'Hot Tub', types: ['hot tubs'],
                         aliases: ['hot tub', 'jacuzzi', 'spa tub', 'inflatable hot tub'], pools: ['outdoor-furniture'] },
    'outdoor-storage': { label: 'Outdoor Storage', types: ['outdoor storage', 'storage boxes', 'coolers'],
                         aliases: ['deck box', 'outdoor storage', 'patio storage', 'cooler'], pools: ['outdoor-furniture'] },

    'fitness':         { label: 'Fitness', types: ['home gym systems', 'free weights', 'yoga mats', 'resistance bands',
                                                   'exercise bikes', 'treadmills', 'weight benches', 'exercise balls',
                                                   'step platforms', 'jump ropes', 'weight accessories', 'body scales'],
                         aliases: ['home gym', 'dumbbell', 'dumbbells', 'free weights', 'yoga mat',
                                   'resistance band', 'resistance bands', 'exercise bike', 'treadmill',
                                   'weight bench', 'exercise ball', 'jump rope', 'workout equipment'], pools: ['all-collections-home-essentials-for-modern-home'] },
    'vacuum':          { label: 'Vacuums & Cleaning', types: ['vacuums', 'robot vacuums & mops', 'carpet cleaners', 'cleaning tools'],
                         aliases: ['vacuum', 'robot vacuum', 'carpet cleaner', 'steam mop', 'cordless vacuum'], pools: ['all-collections-home-essentials-for-modern-home'] },
    'air-purifier':    { label: 'Air & Comfort', types: ['air purifiers', 'fans', 'water filtration'],
                         aliases: ['air purifier', 'purifier', 'fan', 'tower fan', 'water filter'], pools: ['all-collections-home-essentials-for-modern-home'] }
  };

  /* Style vocabulary. `tag` is the private msg-style-* tag written to the
   * catalog; it is intentionally not the public style-* tag so these
   * queries never change what the Shop by Style collections contain. */
  var STYLES = {
    'modern':         { label: 'Modern', tag: 'msg-style-modern',
                        aliases: ['modern', 'contemporary', 'clean lined', 'clean line', 'sleek'] },
    'midcentury':     { label: 'Mid-Century Modern', tag: 'msg-style-midcentury',
                        aliases: ['mid century modern', 'midcentury modern', 'mid-century modern', 'mid century',
                                  'midcentury', 'mcm', 'retro modern', 'danish modern', 'atomic'] },
    'farmhouse':      { label: 'Farmhouse', tag: 'msg-style-farmhouse',
                        aliases: ['modern farmhouse', 'farmhouse', 'farm house', 'rustic', 'country',
                                  'cottage', 'cottagecore', 'shiplap'] },
    'scandinavian':   { label: 'Scandinavian', tag: 'msg-style-scandinavian',
                        aliases: ['scandinavian', 'scandi', 'nordic', 'hygge', 'swedish', 'danish'] },
    'minimalist':     { label: 'Minimalist', tag: 'msg-style-minimalist',
                        aliases: ['minimalist', 'minimalism', 'minimal', 'pared back', 'pared down', 'spare'] },
    'japandi':        { label: 'Japandi', tag: 'msg-style-japandi',
                        aliases: ['japandi', 'zen', 'wabi sabi', 'wabi-sabi', 'japanese inspired'] },
    'organic-modern': { label: 'Organic Modern', tag: 'msg-style-organic-modern',
                        aliases: ['warm organic modern', 'organic modern', 'warm minimal', 'warm minimalist',
                                  'earthy modern', 'organic', 'earthy', 'natural modern'] },
    'bohemian':       { label: 'Bohemian', tag: 'msg-style-bohemian',
                        aliases: ['bohemian', 'boho chic', 'boho', 'eclectic', 'global', 'moroccan'] },
    'coastal':        { label: 'Coastal', tag: 'msg-style-coastal',
                        aliases: ['coastal', 'beachy', 'beach house', 'nautical', 'hamptons', 'seaside', 'coastal grandmother'] },
    'industrial':     { label: 'Industrial', tag: 'msg-style-industrial',
                        aliases: ['industrial', 'loft', 'urban industrial', 'warehouse', 'factory'] },
    'traditional':    { label: 'Traditional', tag: 'msg-style-traditional',
                        aliases: ['traditional', 'classic', 'timeless', 'transitional', 'formal'] },
    'glam':           { label: 'Glam', tag: 'msg-style-glam',
                        aliases: ['glam', 'glamorous', 'hollywood regency', 'art deco', 'deco', 'opulent', 'luxe'] }
  };

  /* Neighbouring styles score partial credit, so a Scandinavian query
   * still surfaces a strong minimalist piece rather than nothing. */
  var STYLE_ADJACENT = {
    'modern':         ['minimalist', 'midcentury', 'scandinavian', 'industrial'],
    'midcentury':     ['modern', 'scandinavian'],
    'farmhouse':      ['traditional', 'coastal'],
    'scandinavian':   ['minimalist', 'japandi', 'modern', 'organic-modern'],
    'minimalist':     ['modern', 'scandinavian', 'japandi'],
    'japandi':        ['minimalist', 'scandinavian', 'organic-modern'],
    'organic-modern': ['japandi', 'scandinavian', 'coastal', 'minimalist'],
    'bohemian':       ['coastal', 'organic-modern'],
    'coastal':        ['farmhouse', 'organic-modern', 'scandinavian'],
    'industrial':     ['modern', 'midcentury'],
    'traditional':    ['farmhouse', 'glam'],
    'glam':           ['traditional', 'modern']
  };

  /* Colour families. `hex` drives the palette strip; `tone` lets the
   * coordinator avoid clashing a warm anchor with a cool accent. */
  var COLORS = {
    'white':       { label: 'White', hex: '#F7F5F1', tone: 'neutral', aliases: ['white', 'bright white', 'off white', 'chalk'] },
    'cream':       { label: 'Cream', hex: '#EFE6D6', tone: 'warm', aliases: ['cream', 'ivory', 'eggshell', 'bone'] },
    'beige':       { label: 'Beige', hex: '#D9CBB6', tone: 'warm', aliases: ['beige', 'tan', 'taupe', 'sand', 'oatmeal', 'linen colored', 'greige', 'camel'] },
    'gray':        { label: 'Gray', hex: '#9C9C99', tone: 'cool', aliases: ['gray', 'grey', 'charcoal', 'slate', 'graphite', 'heather gray'] },
    'black':       { label: 'Black', hex: '#232323', tone: 'neutral', aliases: ['black', 'matte black', 'onyx', 'jet black'] },
    'brown':       { label: 'Brown', hex: '#6B4A32', tone: 'warm', aliases: ['brown', 'chocolate', 'espresso', 'cognac', 'chestnut', 'mocha'] },
    'walnut':      { label: 'Walnut', hex: '#5C4033', tone: 'warm', aliases: ['walnut', 'dark wood', 'mahogany', 'teak stain'] },
    'oak':         { label: 'Light Oak', hex: '#C8A87C', tone: 'warm', aliases: ['light oak', 'white oak', 'oak', 'honey', 'natural wood', 'light wood', 'blonde wood', 'birch', 'maple', 'pine'] },
    'navy':        { label: 'Navy', hex: '#2A3A55', tone: 'cool', aliases: ['navy', 'midnight blue', 'indigo'] },
    'blue':        { label: 'Blue', hex: '#5C7FA3', tone: 'cool', aliases: ['blue', 'sky blue', 'denim', 'cornflower', 'powder blue'] },
    'teal':        { label: 'Teal', hex: '#2E6B6B', tone: 'cool', aliases: ['teal', 'turquoise', 'aqua', 'seafoam'] },
    'green':       { label: 'Green', hex: '#5B6B4F', tone: 'cool', aliases: ['green', 'sage', 'olive', 'emerald', 'forest green', 'moss'] },
    'mustard':     { label: 'Mustard', hex: '#C79A3B', tone: 'warm', aliases: ['mustard', 'yellow', 'ochre', 'gold tone', 'saffron'] },
    'terracotta':  { label: 'Terracotta', hex: '#B5623C', tone: 'warm', aliases: ['terracotta', 'terra cotta', 'rust', 'burnt orange', 'clay', 'orange', 'copper'] },
    'blush':       { label: 'Blush', hex: '#D8AFA6', tone: 'warm', aliases: ['blush', 'pink', 'rose', 'dusty rose', 'mauve'] },
    'red':         { label: 'Red', hex: '#8E3B34', tone: 'warm', aliases: ['red', 'burgundy', 'wine colored', 'crimson', 'oxblood'] },
    'purple':      { label: 'Purple', hex: '#6A5378', tone: 'cool', aliases: ['purple', 'plum', 'lavender', 'lilac', 'aubergine'] },
    'brass':       { label: 'Brass', hex: '#B08D4F', tone: 'warm', aliases: ['brass', 'gold', 'golden', 'antique brass', 'bronze'] },
    'silver':      { label: 'Silver', hex: '#B7BCC0', tone: 'cool', aliases: ['silver', 'chrome', 'nickel', 'stainless', 'polished steel'] },
    /* A group rather than a single family: "neutral" is satisfied by any of
     * the quiet families, which is how shoppers actually mean it. */
    'neutral':     { label: 'Neutrals', hex: '#DCD3C6', tone: 'neutral', group: ['white', 'cream', 'beige', 'gray', 'oak'],
                     aliases: ['warm neutral', 'earth tone', 'earth tones', 'earthy tone', 'earthy tones',
                               'neutrals', 'neutral', 'monochrome'] }
  };

  var MATERIALS = {
    'oak':         { label: 'oak', aliases: ['white oak', 'light oak', 'oak'] },
    'walnut':      { label: 'walnut', aliases: ['walnut'] },
    'acacia':      { label: 'acacia', aliases: ['acacia'] },
    'teak':        { label: 'teak', aliases: ['teak'] },
    'bamboo':      { label: 'bamboo', aliases: ['bamboo'] },
    'wood':        { label: 'wood', aliases: ['solid wood', 'wood', 'wooden', 'timber'] },
    'rattan':      { label: 'rattan', aliases: ['rattan', 'cane', 'caning', 'wicker', 'seagrass', 'water hyacinth'] },
    'jute':        { label: 'jute', aliases: ['jute', 'sisal', 'hemp'] },
    'velvet':      { label: 'velvet', aliases: ['velvet', 'velour'] },
    'boucle':      { label: 'bouclé', aliases: ['boucle', 'bouclé', 'sherpa', 'teddy fabric'] },
    'linen':       { label: 'linen', aliases: ['linen', 'flax'] },
    'cotton':      { label: 'cotton', aliases: ['cotton', 'canvas'] },
    'wool':        { label: 'wool', aliases: ['wool', 'shearling'] },
    'leather':     { label: 'leather', aliases: ['top grain leather', 'genuine leather', 'leather'] },
    'faux-leather':{ label: 'faux leather', aliases: ['faux leather', 'vegan leather', 'pu leather', 'leatherette'] },
    'metal':       { label: 'metal', aliases: ['metal', 'steel', 'iron', 'aluminum', 'aluminium'] },
    'brass':       { label: 'brass', aliases: ['brass', 'bronze'] },
    'glass':       { label: 'glass', aliases: ['tempered glass', 'glass'] },
    'marble':      { label: 'marble', aliases: ['marble', 'faux marble', 'marbled'] },
    'stone':       { label: 'stone', aliases: ['stone', 'travertine', 'slate stone', 'concrete'] },
    'ceramic':     { label: 'ceramic', aliases: ['ceramic', 'stoneware', 'porcelain', 'terracotta clay'] }
  };

  /* Room slot tables — see DATA NOTE at the top of this file. */
  var ROOM_SLOTS = {
    'living-room': {
      min: 400, defaultBudget: 1500,
      slots: [
        { key: 'anchor',   name: 'Anchor seating', share: 40, required: true, lead: true,
          types: ['sofas', 'sectional sofas', 'loveseats', 'daybeds & futons', 'chaise lounges'] },
        { key: 'seating',  name: 'Accent seating', share: 15,
          types: ['accent chairs', 'recliners & armchairs', 'ottomans & poufs', 'benches'] },
        { key: 'tables',   name: 'Tables', share: 10,
          types: ['coffee tables', 'side & end tables', 'console tables'] },
        { key: 'rug',      name: 'Rug & textiles', share: 15,
          types: ['rugs', 'throw pillows', 'pillow covers'] },
        { key: 'lighting', name: 'Lighting', share: 10,
          types: ['table & floor lamps', 'ceiling & pendant lighting'] },
        { key: 'decor',    name: 'Decor', share: 10,
          types: ['wall art', 'mirrors', 'faux plants & trees', 'vases', 'sculptures & objects'] }
      ]
    },
    'bedroom': {
      min: 450, defaultBudget: 1500,
      slots: [
        { key: 'bed',      name: 'Bed frame', share: 28, required: true, lead: true,
          types: ['beds & bed frames', 'headboards'] },
        { key: 'mattress', name: 'Mattress', share: 24, required: true,
          types: ['mattresses'] },
        { key: 'storage',  name: 'Storage', share: 14,
          types: ['dressers', 'cabinets & sideboards', 'storage chests'] },
        { key: 'night',    name: 'Nightstands', share: 9,
          types: ['nightstands', 'side & end tables'] },
        { key: 'bedding',  name: 'Bedding', share: 9,
          types: ['bedding sets', 'sheets & sheet sets'] },
        { key: 'rug',      name: 'Rug', share: 8,
          types: ['rugs'] },
        { key: 'lighting', name: 'Lighting & decor', share: 8,
          types: ['table & floor lamps', 'ceiling & pendant lighting', 'mirrors', 'wall art', 'throw pillows'] }
      ]
    },
    'office': {
      min: 250, defaultBudget: 900,
      slots: [
        { key: 'desk',     name: 'Desk', share: 35, required: true, lead: true, types: ['desks'] },
        { key: 'chair',    name: 'Chair', share: 30, required: true, types: ['office chairs', 'accent chairs'] },
        { key: 'storage',  name: 'Storage', share: 20, types: ['bookcases & shelving'] },
        { key: 'rug',      name: 'Rug & lighting', share: 10, types: ['rugs', 'table & floor lamps'] },
        { key: 'decor',    name: 'Decor', share: 5, types: ['wall art', 'faux plants & trees'] }
      ]
    },
    'dining-room': {
      min: 300, defaultBudget: 1000,
      slots: [
        { key: 'table',    name: 'Dining table', share: 40, required: true, lead: true,
          types: ['dining tables', 'dining sets'] },
        { key: 'seating',  name: 'Dining seating', share: 30, required: true,
          types: ['dining chairs', 'stools & counter stools', 'benches'] },
        { key: 'storage',  name: 'Sideboard', share: 12,
          types: ['cabinets & sideboards', 'console tables'] },
        { key: 'lighting', name: 'Lighting', share: 10,
          types: ['ceiling & pendant lighting', 'table & floor lamps'] },
        { key: 'table-top', name: 'Table setting', share: 8,
          types: ['dinnerware', 'flatware', 'table linens', 'vases', 'candles & holders'] }
      ]
    },
    'kitchen': {
      min: 200, defaultBudget: 800,
      note: 'Built around appliances and tableware rather than furniture, so this is an upgrade rather than a full furnishing.',
      slots: [
        { key: 'major',    name: 'Major appliance', share: 40, required: true, lead: true,
          types: ['refrigerators', 'chest freezers', 'beverage & wine coolers', 'ice makers', 'mini fridges'] },
        { key: 'counter',  name: 'Counter appliance', share: 25, required: true,
          types: ['coffee & espresso machines', 'air fryers', 'blenders & mixers', 'bread makers',
                  'toasters & toaster ovens', 'electric kettles', 'juicers', 'specialty kitchen appliances',
                  'ice cream makers', 'pasta makers', 'tea makers', 'yogurt makers'] },
        { key: 'cook',     name: 'Cookware & knives', share: 15,
          types: ['cookware', 'knives & knife storage', 'cutting boards'] },
        { key: 'table',    name: 'Tableware', share: 12,
          types: ['dinnerware', 'flatware', 'bowls', 'drinking glasses', 'wine glasses & stemware',
                  'mugs', 'drinkware', 'teapots & coffee servers'] },
        { key: 'dining',   name: 'Dining seating', share: 8,
          types: ['dining chairs', 'stools & counter stools', 'dining tables', 'dining sets'] }
      ]
    },
    'bathroom': {
      min: 60, max: 400, defaultBudget: 250,
      note: 'Bathroom pieces are small-ticket items, so this room is capped at $400.',
      slots: [
        { key: 'storage',  name: 'Storage', share: 25, required: true, lead: true,
          types: ['bath storage', 'baskets & bins', 'wall organizers'] },
        { key: 'towels',   name: 'Towels', share: 25, required: true, types: ['bath towels'] },
        { key: 'mat',      name: 'Bath mat', share: 15, types: ['bath mats'] },
        { key: 'curtain',  name: 'Shower curtain', share: 15, types: ['shower curtains'] },
        { key: 'access',   name: 'Accessories', share: 20,
          types: ['bath accessories', 'soap dispensers', 'towel bars & hooks', 'home fragrance', 'body scales'] }
      ]
    },
    'entryway': {
      min: 150, defaultBudget: 500,
      slots: [
        { key: 'anchor',   name: 'Console or bench', share: 40, required: true, lead: true,
          types: ['console tables', 'benches', 'cabinets & sideboards'] },
        { key: 'mirror',   name: 'Mirror', share: 20, types: ['mirrors'] },
        { key: 'rug',      name: 'Runner or rug', share: 15, types: ['rugs'] },
        { key: 'storage',  name: 'Storage', share: 13,
          types: ['baskets & bins', 'wall organizers', 'storage boxes', 'bookcases & shelving'] },
        { key: 'decor',    name: 'Decor', share: 12,
          types: ['wall art', 'faux plants & trees', 'vases', 'table & floor lamps', 'candles & holders'] }
      ]
    },
    'outdoor': {
      min: 300, defaultBudget: 1200,
      slots: [
        { key: 'seating',  name: 'Seating', share: 45, required: true, lead: true,
          types: ['patio furniture sets', 'patio dining sets', 'patio seating', 'patio benches', 'patio ottomans'] },
        { key: 'table',    name: 'Table', share: 20, types: ['patio tables', 'dining tables'] },
        { key: 'feature',  name: 'Fire pit or grill', share: 25, types: ['fire pits', 'grills', 'food smokers'] },
        { key: 'decor',    name: 'Decor & storage', share: 10,
          types: ['planters & plant stands', 'water features', 'table & floor lamps',
                  'outdoor storage', 'storage boxes', 'coolers'] }
      ]
    }
  };

  /* A "look" board when only a style is named: one piece per line,
   * ordered so the board reads like a room rather than a category list. */
  var LOOK_LINES = [
    { name: 'Seating',  types: ['sofas', 'sectional sofas', 'loveseats', 'accent chairs', 'recliners & armchairs'] },
    { name: 'Tables',   types: ['coffee tables', 'side & end tables', 'console tables', 'dining tables'] },
    { name: 'Textiles', types: ['rugs', 'throw pillows', 'pillow covers', 'bedding sets', 'curtains & drapes'] },
    { name: 'Lighting', types: ['table & floor lamps', 'ceiling & pendant lighting'] },
    /* Vanities carry the product_type of cabinets, so they are excluded by
     * title here: a style board is not the place for a bathroom vanity. */
    { name: 'Storage',  types: ['bookcases & shelving', 'cabinets & sideboards', 'dressers', 'nightstands'],
      titleNot: ['vanity', 'vanities'] },
    { name: 'Finishing touches', types: ['wall art', 'mirrors', 'vases', 'faux plants & trees',
                                         'sculptures & objects', 'candles & holders', 'clocks'] }
  ];

  /* Gift lanes. Interests map to product types that actually exist in
   * the catalog, which is where the old Gift Finder was weakest. */
  var GIFT_LANES = {
    'cooking':   { label: 'the cook', types: ['cookware', 'knives & knife storage', 'cutting boards', 'air fryers',
                                              'blenders & mixers', 'bread makers', 'slow cookers', 'pressure cookers',
                                              'indoor grills & griddles', 'sous vide'],
                   aliases: ['cook', 'cooking', 'chef', 'baker', 'baking', 'foodie', 'kitchen'] },
    'coffee':    { label: 'the coffee drinker', types: ['coffee & espresso machines', 'electric kettles', 'mugs',
                                                        'teapots & coffee servers', 'tea makers', 'coffee accessories', 'tumblers'],
                   aliases: ['coffee', 'espresso', 'latte', 'tea', 'caffeine'] },
    'host':      { label: 'the host', types: ['dinnerware', 'flatware', 'wine glasses & stemware', 'drinking glasses',
                                              'bowls', 'cake stands & servers', 'table linens', 'beverage & wine coolers',
                                              'ice makers', 'candles & holders'],
                   aliases: ['host', 'hostess', 'entertainer', 'entertaining', 'dinner party', 'wine'] },
    'cozy':      { label: 'the homebody', types: ['throw pillows', 'pillow covers', 'bedding sets', 'home fragrance',
                                                  'candles & holders', 'electric fireplaces', 'rugs'],
                   aliases: ['cozy', 'cosy', 'comfort', 'relaxing', 'self care', 'spa', 'homebody'] },
    'fitness':   { label: 'the one who trains', types: ['home gym systems', 'free weights', 'yoga mats', 'resistance bands',
                                                        'exercise bikes', 'treadmills', 'weight benches', 'exercise balls',
                                                        'jump ropes', 'body scales', 'tumblers'],
                   aliases: ['fitness', 'gym', 'workout', 'training', 'runner', 'yoga', 'peloton', 'lifting'] },
    'decor':     { label: 'the decorator', types: ['vases', 'wall art', 'mirrors', 'faux plants & trees',
                                                   'sculptures & objects', 'decorative trays', 'clocks', 'candles & holders'],
                   aliases: ['decor', 'decorating', 'design', 'home decor', 'interiors', 'plants'] },
    'office':    { label: 'the desk worker', types: ['desks', 'office chairs', 'table & floor lamps', 'desk accessories',
                                                     'speakers & audio', 'bookcases & shelving'],
                   aliases: ['office', 'desk', 'work', 'wfh', 'student', 'gamer', 'reader', 'books'] },
    'outdoor':   { label: 'the outdoor entertainer', types: ['grills', 'food smokers', 'fire pits', 'coolers',
                                                             'patio seating', 'water features', 'planters & plant stands'],
                   aliases: ['grilling', 'bbq', 'barbecue', 'outdoors', 'patio', 'camping', 'gardening', 'garden'] },
    'clean':     { label: 'the practical one', types: ['vacuums', 'robot vacuums & mops', 'air purifiers',
                                                       'water filtration', 'food storage containers', 'kitchen canisters',
                                                       'baskets & bins', 'carpet cleaners'],
                   aliases: ['practical', 'clean', 'cleaning', 'organizing', 'organised', 'organized', 'tidy', 'new home', 'housewarming'] }
  };

  var OCCASIONS = ['birthday', 'anniversary', 'wedding', 'housewarming', 'christmas', 'holiday',
                   'mothers day', 'mother day', 'fathers day', 'father day', 'graduation',
                   'valentines', 'valentine', 'retirement', 'baby shower', 'engagement'];

  var RECIPIENTS = ['mom', 'mother', 'mum', 'dad', 'father', 'wife', 'husband', 'partner',
                    'girlfriend', 'boyfriend', 'sister', 'brother', 'daughter', 'son',
                    'grandma', 'grandmother', 'grandpa', 'grandfather', 'friend', 'coworker',
                    'co worker', 'colleague', 'boss', 'teacher', 'in laws', 'newlyweds',
                    'couple', 'roommate', 'neighbor', 'neighbour', 'host', 'hostess'];

  var GIFT_SIGNALS = ['gift', 'gifts', 'gift idea', 'gift ideas', 'present for', 'presents',
                      'buy for', 'shopping for', 'something for', 'gifting', 'registry'];

  /* ===================================================================
   * 2. PARSER
   * Longest phrases are consumed first and blanked out of the working
   * string, so "mid century modern" never also fires "modern", and
   * "entryway table" never also fires "entryway" as a room.
   * ================================================================ */

  function normalize(s) {
    return ' ' + String(s || '')
      .toLowerCase()
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/'s\b/g, '')          // "my dad's birthday" -> "my dad birthday"
      /* Thousands separators go before punctuation is flattened to spaces.
       * Without this, "$2,000" became "$2 000" and the budget parsed as two
       * dollars — which then silently reshaped every allowance in the set. */
      .replace(/(\d),(?=\d{3}\b)/g, '$1')
      .replace(/[^a-z0-9$.\- ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() + ' ';
  }

  function blank(n) {
    var s = '';
    while (s.length < n) s += ' ';
    return s;
  }

  /* Finds `phrase` on a word boundary and blanks it so later, shorter
   * patterns cannot match the same characters twice. */
  function take(state, phrase) {
    var needle = ' ' + phrase.replace(/\s+/g, ' ') + ' ';
    var i = state.text.indexOf(needle);
    if (i === -1) {
      // tolerate a trailing plural on the last word
      needle = ' ' + phrase + 's ';
      i = state.text.indexOf(needle);
      if (i === -1) return false;
    }
    state.text = state.text.slice(0, i + 1) + blank(needle.length - 2) + state.text.slice(i + needle.length - 1);
    return true;
  }

  function byLengthDesc(a, b) { return b.length - a.length; }

  /* Once a vocabulary key has matched, swallow its other aliases too, so
   * "sage green" does not leave "sage" behind after "green" matched. */
  function takeAllAliases(state, vocab, key) {
    (vocab[key].aliases || []).slice().sort(byLengthDesc).forEach(function (a) {
      take(state, a);
    });
  }

  /* Every alias across a vocabulary, longest first, paired with its key. */
  function aliasIndex(vocab) {
    var out = [];
    Object.keys(vocab).forEach(function (key) {
      (vocab[key].aliases || []).forEach(function (a) { out.push({ key: key, alias: a }); });
    });
    out.sort(function (a, b) { return b.alias.length - a.alias.length; });
    return out;
  }

  var ROOM_INDEX = aliasIndex(ROOMS);
  var CATEGORY_INDEX = aliasIndex(CATEGORIES);
  var STYLE_INDEX = aliasIndex(STYLES);
  var COLOR_INDEX = aliasIndex(COLORS);
  var MATERIAL_INDEX = aliasIndex(MATERIALS);
  var GIFT_LANE_INDEX = aliasIndex(GIFT_LANES);

  function parseBudget(state) {
    var t = state.text;
    var m;

    // a range: "$500-800", "between 500 and 800", "$500 to $800"
    m = t.match(/\$?\s*(\d[\d,]*(?:\.\d+)?)\s*(?:-|to|and)\s*\$?\s*(\d[\d,]*(?:\.\d+)?)/);
    if (m && /between|-|\bto\b|\band\b/.test(m[0])) {
      var lo = parseFloat(m[1].replace(/,/g, ''));
      var hi = parseFloat(m[2].replace(/,/g, ''));
      // Guard against dimensions like "12 to 14 ft" and against x-by-x sizes.
      if (hi > lo && hi >= 40 && !/\b(ft|feet|foot|inch|inches|in)\b/.test(t.slice(t.indexOf(m[0]) + m[0].length, t.indexOf(m[0]) + m[0].length + 8))) {
        take(state, m[0].trim());
        return { min: lo, max: hi, kind: 'range' };
      }
    }

    m = t.match(/(?:under|below|less than|no more than|max|maximum|up to|within|cheaper than)\s*\$?\s*(\d[\d,]*(?:\.\d+)?)\s*(?:k\b)?/);
    if (m) {
      var v = parseFloat(m[1].replace(/,/g, ''));
      if (/k\b/.test(m[0])) v *= 1000;
      take(state, m[0].trim());
      return { max: v, kind: 'under' };
    }

    /* "around $900" and the bare "for 700" both land here. The bare form
     * needs a floor of $40 so "seats 6" and "for 2 people" are not read
     * as money. */
    m = t.match(/(?:around|about|roughly|approximately|near|circa|budget of|budget is|budget|spend|for)\s*\$?\s*(\d[\d,]*(?:\.\d+)?)\s*(k\b)?/);
    if (m && (m[0].indexOf('$') !== -1 || m[2] || parseFloat(m[1].replace(/,/g, '')) >= 40)) {
      var a = parseFloat(m[1].replace(/,/g, ''));
      if (m[2]) a *= 1000;
      take(state, m[0].trim());
      return { target: a, min: a * 0.75, max: a * 1.1, kind: 'around' };
    }

    m = t.match(/\$\s*(\d[\d,]*(?:\.\d+)?)\s*(k\b)?/);
    if (m) {
      var b = parseFloat(m[1].replace(/,/g, ''));
      if (m[2]) b *= 1000;
      take(state, m[0].trim());
      return { target: b, min: b * 0.75, max: b * 1.1, kind: 'around' };
    }

    m = t.match(/(\d[\d,]*)\s*(?:k\b|thousand)\s*(?:dollars?|budget)?/);
    if (m) {
      var c = parseFloat(m[1].replace(/,/g, '')) * 1000;
      take(state, m[0].trim());
      return { target: c, min: c * 0.75, max: c * 1.1, kind: 'around' };
    }

    m = t.match(/(\d[\d,]*(?:\.\d+)?)\s*(?:dollars|bucks)/);
    if (m) {
      var d = parseFloat(m[1].replace(/,/g, ''));
      take(state, m[0].trim());
      return { target: d, min: d * 0.75, max: d * 1.1, kind: 'around' };
    }

    /* Last resort: a bare number on its own, as in "living room sectional
     * industrial 3500". It has to clear $100 and carry no unit, so counts
     * ("set of 4"), sizes ("24 inch", "8x10") and capacities ("seats 6")
     * are not mistaken for money. */
    m = t.match(/(?:^|\s)(\d{3,6})(?:\s|$)/);
    if (m) {
      var e = parseFloat(m[1]);
      var at = t.indexOf(m[0]);
      var after = t.slice(at + m[0].length - 1, at + m[0].length + 10);
      var before = t.slice(Math.max(0, at - 6), at + 1);
      var unitish = /^\s*(ft|feet|foot|inch|inches|in|cm|mm|lb|lbs|pound|sq|square|watt|w|k\b|piece|pc|pcs|count|ct|thread|x)\b/.test(after);
      var dimish = /(x|by)\s*$/.test(before);
      if (e >= 100 && e <= 100000 && !unitish && !dimish) {
        take(state, m[1]);
        return { target: e, min: e * 0.75, max: e * 1.1, kind: 'around' };
      }
    }

    return null;
  }

  /* An "8x10" is room dimensions in "my 12x14 living room" but a product
   * size in "rug 8x10". Which one it is depends on whether the shopper
   * named a specific piece. */
  function parseDimensions(state, hasCategory) {
    var m = state.text.match(/(\d{1,2}(?:\.\d)?)\s*(?:x|by)\s*(\d{1,2}(?:\.\d)?)\s*(?:ft|feet|foot)?/);
    if (!m) return null;
    var a = parseFloat(m[1]), b = parseFloat(m[2]);
    if (a < 4 || b < 4 || a > 60 || b > 60) return null;
    take(state, m[0].trim());
    return {
      length: Math.max(a, b), width: Math.min(a, b),
      kind: hasCategory ? 'product' : 'room'
    };
  }

  /* Bed and mattress sizes, matched longest-first so "california king"
   * never reduces to "king". Mirrors the Space Planner's size handling. */
  var BED_SIZES = [
    { key: 'california king', label: 'California King' },
    { key: 'cal king', label: 'California King' },
    { key: 'twin xl', label: 'Twin XL' },
    { key: 'king', label: 'King' },
    { key: 'queen', label: 'Queen' },
    { key: 'full', label: 'Full' },
    { key: 'double', label: 'Full' },
    { key: 'twin', label: 'Twin' }
  ];

  function parseBedSize(state) {
    for (var i = 0; i < BED_SIZES.length; i++) {
      if (take(state, BED_SIZES[i].key)) return BED_SIZES[i].label;
    }
    return null;
  }

  /* Mood words. These are real signals about finish and palette even
   * though they are not colours, so they steer the ranking rather than
   * being discarded as noise. */
  var TONE_WORDS = {
    warm:  ['warm', 'cozy', 'cosy', 'inviting', 'snug', 'earthy', 'rich'],
    light: ['airy', 'bright', 'light and bright', 'fresh', 'breezy', 'soft'],
    dark:  ['moody', 'dramatic', 'dark', 'bold', 'masculine'],
    calm:  ['calm', 'serene', 'restful', 'peaceful', 'spa like', 'quiet']
  };

  var SCALE_WORDS = {
    small: ['small', 'tiny', 'compact', 'narrow', 'apartment', 'studio apartment', 'condo',
            'dorm', 'cramped', 'little', 'snug'],
    large: ['large', 'big', 'huge', 'spacious', 'open concept', 'open plan', 'oversized', 'great big']
  };

  function parseIntent(raw) {
    var state = { text: normalize(raw) };
    var intent = {
      raw: String(raw || ''),
      budget: null, dims: null, scale: null,
      room: null, categories: [], styles: [], colors: [], materials: [],
      gift: false, recipient: null, occasion: null, lanes: [],
      mode: null, unmatched: []
    };

    intent.budget = parseBudget(state);

    GIFT_SIGNALS.slice().sort(byLengthDesc).forEach(function (g) {
      if (take(state, g)) intent.gift = true;
    });
    OCCASIONS.slice().sort(byLengthDesc).forEach(function (o) {
      if (!intent.occasion && take(state, o)) { intent.occasion = o; intent.gift = true; }
    });
    RECIPIENTS.slice().sort(byLengthDesc).forEach(function (r) {
      if (!intent.recipient && take(state, r)) { intent.recipient = r; intent.gift = true; }
    });

    // Categories before rooms: "entryway table" is a console, not the entry room.
    CATEGORY_INDEX.forEach(function (e) {
      if (intent.categories.indexOf(e.key) !== -1) return;
      if (take(state, e.alias)) intent.categories.push(e.key);
    });

    ROOM_INDEX.forEach(function (e) {
      if (intent.room) return;
      if (take(state, e.alias)) intent.room = e.key;
    });

    STYLE_INDEX.forEach(function (e) {
      if (intent.styles.indexOf(e.key) !== -1) return;
      if (take(state, e.alias)) {
        if (intent.styles.length < 2) intent.styles.push(e.key);
        takeAllAliases(state, STYLES, e.key);
      }
    });

    intent.dims = parseDimensions(state, intent.categories.length > 0);

    /* Only read a size word as a bed size when the request is actually
     * about sleeping, so "full length mirror" and "double oven" are safe. */
    var sleepish = intent.room === 'bedroom' ||
      ['bed', 'mattress', 'bedding', 'nightstand'].some(function (c) {
        return intent.categories.indexOf(c) !== -1;
      });
    intent.bedSize = sleepish ? parseBedSize(state) : null;

    COLOR_INDEX.forEach(function (e) {
      if (intent.colors.indexOf(e.key) !== -1) return;
      if (take(state, e.alias)) {
        intent.colors.push(e.key);
        takeAllAliases(state, COLORS, e.key);
      }
    });

    MATERIAL_INDEX.forEach(function (e) {
      if (intent.materials.indexOf(e.key) !== -1) return;
      if (take(state, e.alias)) {
        intent.materials.push(e.key);
        takeAllAliases(state, MATERIALS, e.key);
      }
    });

    Object.keys(TONE_WORDS).forEach(function (k) {
      TONE_WORDS[k].slice().sort(byLengthDesc).forEach(function (w) {
        if (take(state, w) && !intent.tone) intent.tone = k;
      });
    });

    if (intent.gift) {
      GIFT_LANE_INDEX.forEach(function (e) {
        if (intent.lanes.indexOf(e.key) === -1 && take(state, e.alias)) intent.lanes.push(e.key);
      });
    }

    Object.keys(SCALE_WORDS).forEach(function (k) {
      SCALE_WORDS[k].slice().sort(byLengthDesc).forEach(function (w) {
        if (take(state, w) && !intent.scale) intent.scale = k;
      });
    });

    /* A bare "chair" in an office request means the desk chair, unless the
     * shopper actually said "accent chair". */
    if (intent.room === 'office') {
      var ai = intent.categories.indexOf('accent-chair');
      if (ai !== -1 && !/accent|lounge|reading|recliner/.test(normalize(raw))) {
        intent.categories[ai] = 'office-chair';
      }
    }

    var WHOLE_ROOM = ['furnish', 'furnishing', 'decorate', 'decorating', 'set up', 'setup',
                      'redo', 'redoing', 'makeover', 'make over', 'refresh', 'restyle',
                      'design', 'designing', 'from scratch', 'whole', 'entire', 'complete',
                      'everything for', 'starting over', 'moving into', 'new place'];
    var wholeRoom = false;
    WHOLE_ROOM.slice().sort(byLengthDesc).forEach(function (w) {
      if (take(state, w)) wholeRoom = true;
    });
    intent.wholeRoom = wholeRoom;

    // Whatever survived the passes above is a word we did not understand.
    intent.unmatched = state.text.split(/\s+/).filter(function (w) {
      return w && w.length > 2 && STOPWORDS.indexOf(w) === -1;
    });

    intent.mode = inferMode(intent);
    return intent;
  }

  var STOPWORDS = ['the', 'and', 'for', 'with', 'that', 'this', 'have', 'has', 'need', 'want',
                   'looking', 'look', 'find', 'show', 'get', 'some', 'something', 'anything',
                   'nice', 'good', 'best', 'great', 'really', 'very', 'help', 'please', 'like',
                   'love', 'would', 'could', 'should', 'about', 'into', 'from', 'but', 'not',
                   'our', 'your', 'their', 'his', 'her', 'its', 'you', 'ive', 'im', 'its',
                   'new', 'old', 'more', 'less', 'also', 'just', 'can', 'will', 'give',
                   'ideas', 'idea', 'options', 'option', 'style', 'styles', 'vibe', 'feel',
                   'space', 'room', 'home', 'house', 'place', 'who', 'loves', 'love',
                   'seats', 'seat', 'people', 'person', 'both', 'all', 'any', 'them',
                   'she', 'he', 'they', 'set', 'sets', 'piece', 'pieces', 'thing', 'things',
                   'shop', 'buy', 'sale', 'deal', 'deals', 'upgrade', 'inspiration'];

  function inferMode(intent) {
    if (intent.gift) return 'gift';
    if (intent.wholeRoom && intent.room) return 'room';
    if (intent.categories.length) return 'item';
    if (intent.room && (intent.budget || intent.dims)) return 'room';
    if (intent.room) return 'room';
    if (intent.styles.length) return 'look';
    return null;
  }

  /* ===================================================================
   * 3. CATALOG
   * Collections are fetched from the public products.json endpoint and
   * cached for the page session. Each product is reduced once to the
   * signals the ranker needs, so scoring never re-parses HTML.
   * ================================================================ */

  /* The catalog arrives as one prebuilt index rather than by paginating
   * products.json per collection. That decision came out of measuring the
   * real payloads: a compact index of all 1,983 published products is
   * 208 KB gzipped, while a single room collection fetched from
   * products.json is 311 KB gzipped. One request covers the whole store,
   * so a query is never limited to the collection a shopper happened to
   * imply, and fitness and cleaning products — which belong to no room
   * collection at all — become reachable.
   *
   * The index is generated by running reduceProduct (below) over the live
   * catalog in Node, so the browser and the build share one definition of
   * how a product's colour, material and style are read.
   *
   * Prices in the index are a snapshot. After rendering, the prices of the
   * items actually on screen are re-checked against the live product JSON
   * and quietly corrected, so nothing stale is ever shown for long. */

  var INDEX_URL = null;      // set from the section markup
  var indexPromise = null;

  function stripHtml(html) {
    return String(html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&[a-z]+;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Reads a vocabulary out of free text. Longest aliases first with
   * blanking, the same discipline as the query parser, so "light oak"
   * does not also register as generic "oak" plus "light". */
  function detectAll(text, index, vocab, limit) {
    var state = { text: normalize(text) };
    var found = [];
    for (var i = 0; i < index.length; i++) {
      var e = index[i];
      if (found.indexOf(e.key) !== -1) continue;
      if (take(state, e.alias)) {
        found.push(e.key);
        takeAllAliases(state, vocab, e.key);
        if (limit && found.length >= limit) break;
      }
    }
    return found;
  }

  /* Full reduction from a products.json record. Used at build time. */
  function reduceProduct(p) {
    var title = p.title || '';
    var type = String(p.product_type || '').toLowerCase().trim();
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });

    var variant = (p.variants || [])[0] || {};
    var price = parseFloat(variant.price != null ? variant.price : p.price);
    if (isNaN(price)) price = 0;

    var image = null;
    if (p.images && p.images.length && p.images[0].src) image = p.images[0].src;
    else if (p.featured_image) image = p.featured_image;

    /* Style comes only from the private msg-style-* tags. The public
     * style-* tags are deliberately left out of this. */
    var styles = [];
    tags.forEach(function (t) {
      Object.keys(STYLES).forEach(function (k) {
        if (t === STYLES[k].tag && styles.indexOf(k) === -1) styles.push(k);
      });
    });

    /* Colour and material are read from the title first because that is
     * the reliable signal; the opening of the description is a fallback,
     * capped so a long listing cannot dominate. */
    var body = stripHtml(p.body_html).slice(0, 500);
    var colors = detectAll(title, COLOR_INDEX, COLORS, 3);
    if (!colors.length) colors = detectAll(body, COLOR_INDEX, COLORS, 2);
    var materials = detectAll(title, MATERIAL_INDEX, MATERIALS, 3);
    if (!materials.length) materials = detectAll(body, MATERIAL_INDEX, MATERIALS, 2);

    var sizes = detectSizes(title) || detectSizes(body);

    return {
      handle: p.handle, title: title, type: type, price: price, image: image,
      styles: styles,
      colors: colors.filter(function (c) { return c !== 'neutral'; }),
      materials: materials,
      sizes: sizes
    };
  }

  /* Which bed sizes a listing actually evidences. Several sizes means the
   * listing genuinely offers a choice, which is worth saying out loud
   * rather than guessing at. */
  function detectSizes(text) {
    var state = { text: normalize(text) };
    var found = [];
    BED_SIZES.forEach(function (s) {
      if (take(state, s.key) && found.indexOf(s.label) === -1) found.push(s.label);
    });
    return found.length ? found : null;
  }

  /* Index row order. Kept positional to hold the file small. */
  function rowToProduct(r) {
    return {
      handle: r[0],
      title: r[1],
      url: '/products/' + r[0],
      type: r[2],
      price: r[3],
      image: r[4],
      styles: r[5] || [],
      colors: r[6] || [],
      materials: r[7] || [],
      sizes: r[8] || null,
      titleLower: String(r[1]).toLowerCase(),
      hay: (String(r[1]) + ' ' + r[2]).toLowerCase()
    };
  }

  function productToRow(p) {
    return [p.handle, p.title, p.type, Math.round(p.price * 100) / 100, p.image,
            p.styles, p.colors, p.materials, p.sizes];
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = fetch(INDEX_URL)
      .then(function (r) {
        if (!r.ok) throw new Error('index ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return {
          generated: data.generated || null,
          products: (data.rows || []).map(rowToProduct)
        };
      });
    return indexPromise;
  }

  /* ===================================================================
   * 4. RANKING
   * A score plus the reasons behind it. The reasons are not decoration:
   * they are emitted by the same clauses that moved the score, so what
   * the shopper reads is what actually happened.
   * ================================================================ */

  function typeMatches(product, types) {
    return types.indexOf(product.type) !== -1;
  }

  /* Some product types are broader than the slot that wants them —
   * "cabinets & sideboards" contains bathroom vanities — so a config can
   * exclude by title as well as select by type. */
  function passesTitleFilters(product, cfg) {
    if (cfg.titleNot && cfg.titleNot.some(function (w) { return product.titleLower.indexOf(w) !== -1; })) return false;
    if (cfg.titleAny && !cfg.titleAny.some(function (w) { return product.titleLower.indexOf(w) !== -1; })) return false;
    return true;
  }

  function slotFilter(cfg) {
    return function (product) {
      return typeMatches(product, cfg.types) && passesTitleFilters(product, cfg);
    };
  }

  /* A brand guess from the leading token of the title. Good enough to stop
   * one seller filling a row; not used for anything the shopper sees. */
  function brandOf(product) {
    return product.titleLower.split(/[\s,|]+/)[0] || product.handle;
  }

  /* Keeps a row varied. Without this, one product type and one seller take
   * over: a style board's textiles row came back as three $18 pillow covers
   * from the same brand, which is technically well matched and useless to
   * look at. */
  function dedupeVariety(scored, limit, perType, perBrand) {
    var typeCount = {}, brandCount = {}, out = [];
    for (var i = 0; i < scored.length && out.length < limit; i++) {
      var p = scored[i].product;
      var b = brandOf(p);
      if (perType && (typeCount[p.type] || 0) >= perType) continue;
      if (perBrand && (brandCount[b] || 0) >= perBrand) continue;
      typeCount[p.type] = (typeCount[p.type] || 0) + 1;
      brandCount[b] = (brandCount[b] || 0) + 1;
      out.push(scored[i]);
    }
    /* The caps are deliberately allowed to produce a short row. Padding a
     * thin category back up to the limit is what put a second identical rug
     * and a red hearts pillow into a Japandi board — two fewer, better
     * pieces is the honest result. The fallback only rescues an empty row. */
    if (!out.length && scored.length) out.push(scored[0]);
    return out;
  }

  function categoryMatches(product, catKey) {
    var cat = CATEGORIES[catKey];
    if (!cat) return false;
    if (!typeMatches(product, cat.types)) return false;
    /* Table & Floor Lamps is one product_type covering two very different
     * pieces, so those categories also test the title. */
    return passesTitleFilters(product, cat);
  }

  function colorSatisfied(product, wanted) {
    var group = COLORS[wanted] && COLORS[wanted].group;
    if (group) {
      return product.colors.some(function (c) { return group.indexOf(c) !== -1; });
    }
    return product.colors.indexOf(wanted) !== -1;
  }

  var TONE_TARGETS = {
    warm:  ['cream', 'beige', 'brown', 'walnut', 'oak', 'terracotta', 'mustard', 'brass', 'blush'],
    light: ['white', 'cream', 'beige', 'oak'],
    dark:  ['black', 'gray', 'navy', 'walnut', 'brown'],
    calm:  ['white', 'cream', 'beige', 'gray', 'green', 'blue', 'oak']
  };

  /* Scores one product against the intent. `anchor` carries the style and
   * palette already committed to by the lead piece of a set. */
  function scoreProduct(product, intent, anchor) {
    var score = 0;
    var reasons = [];

    // ---- style -------------------------------------------------------
    var wantStyles = (anchor && anchor.styles && anchor.styles.length) ? anchor.styles : intent.styles;
    if (wantStyles && wantStyles.length) {
      var direct = wantStyles.filter(function (s) { return product.styles.indexOf(s) !== -1; });
      if (direct.length) {
        score += 6;
        reasons.push(STYLES[direct[0]].label);
      } else {
        var near = [];
        wantStyles.forEach(function (s) {
          (STYLE_ADJACENT[s] || []).forEach(function (n) {
            if (product.styles.indexOf(n) !== -1 && near.indexOf(n) === -1) near.push(n);
          });
        });
        if (near.length) {
          score += 2;
          reasons.push(STYLES[near[0]].label + ', a close neighbour of ' + STYLES[wantStyles[0]].label);
        } else if (product.styles.length) {
          score -= 1;   // tagged as a different look
        }
      }
    } else if (product.styles.length) {
      score += 0.5;     // a known look beats an unknown one, faintly
    }

    // ---- colour ------------------------------------------------------
    var wantColors = intent.colors.slice();
    if (anchor && anchor.colors) {
      anchor.colors.forEach(function (c) { if (wantColors.indexOf(c) === -1) wantColors.push(c); });
    }
    if (wantColors.length) {
      var hit = wantColors.filter(function (c) { return colorSatisfied(product, c); });
      if (hit.length) {
        score += 4;
        var label = COLORS[hit[0]].label;
        reasons.push(anchor && intent.colors.indexOf(hit[0]) === -1
          ? label + ', picking up the anchor piece'
          : label);
      } else if (intent.colors.length) {
        score -= 1.5;
      }
    }

    // ---- tone --------------------------------------------------------
    if (intent.tone && TONE_TARGETS[intent.tone]) {
      var toneHit = product.colors.filter(function (c) {
        return TONE_TARGETS[intent.tone].indexOf(c) !== -1;
      });
      if (toneHit.length) {
        score += 1.5;
        if (reasons.length < 2) reasons.push(COLORS[toneHit[0]].label + ' reads ' + intent.tone);
      }
    }

    // ---- material ----------------------------------------------------
    if (intent.materials && intent.materials.length) {
      var mat = intent.materials.filter(function (m) { return product.materials.indexOf(m) !== -1; });
      if (mat.length) {
        score += 3;
        reasons.push(MATERIALS[mat[0]].label);
      } else {
        score -= 1;
      }
    }

    // ---- bed size ----------------------------------------------------
    /* Most listings here sell several sizes from one page, so a size is
     * treated as evidence rather than a filter. A listing that names the
     * size wins; one that names other sizes is still shown, because the
     * size the shopper wants is usually a dropdown away. */
    if (intent.bedSize) {
      var wantSize = intent.bedSize.toLowerCase();
      var listed = product.sizes || [];
      var namesIt = listed.some(function (s) { return s.toLowerCase() === wantSize; });
      if (namesIt) {
        score += 3;
        reasons.push(listed.length > 1
          ? intent.bedSize + ' among ' + listed.length + ' sizes offered'
          : intent.bedSize);
      } else if (listed.length) {
        score += 0.5;   // sized listing, other sizes named
      }
    }

    // ---- price -------------------------------------------------------
    var b = intent.budget;
    if (b) {
      var cap = b.max != null ? b.max : (b.target != null ? b.target * 1.1 : null);
      var floor = b.min != null ? b.min : null;
      if (cap != null && product.price > cap) return null;             // hard out
      if (floor != null && product.price < floor * 0.5) score -= 1;    // oddly cheap for the ask
      if (cap != null) {
        var ratio = product.price / cap;
        if (ratio >= 0.55) { score += 1; reasons.push('$' + product.price.toFixed(0) + ', using the budget well'); }
        else if (ratio >= 0.25) { score += 0.5; reasons.push('$' + product.price.toFixed(0) + ', comfortably under'); }
        else { reasons.push('$' + product.price.toFixed(0) + ', leaves room to spend elsewhere'); }
      }
    }

    if (score <= -2) return null;

    /* Never show a card with nothing said about it. If none of the clauses
     * above fired, fall back to what is plainly true of the product. */
    if (!reasons.length) {
      if (product.materials.length) reasons.push(MATERIALS[product.materials[0]].label);
      else if (product.colors.length) reasons.push(COLORS[product.colors[0]].label);
      if (reasons.length < 2) reasons.push('$' + product.price.toFixed(0));
    }

    return { product: product, score: score, reasons: reasons };
  }

  function rank(products, intent, anchor, filterFn) {
    var out = [];
    products.forEach(function (p) {
      if (filterFn && !filterFn(p)) return;
      var s = scoreProduct(p, intent, anchor);
      if (s) out.push(s);
    });
    /* Ties break on price descending: on equal evidence, the piece that
     * uses more of the budget is usually the better piece. */
    out.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return b.product.price - a.product.price;
    });
    return out;
  }

  /* ===================================================================
   * 5. COORDINATION
   * Budget allocation reuses the Budget Planner's share model. The extra
   * step here is that the lead slot is filled first and its style and
   * colour become the anchor every later slot is judged against, which
   * is what makes the result a set rather than six separate searches.
   * ================================================================ */

  function budgetFor(intent, roomKey) {
    var cfg = ROOM_SLOTS[roomKey];
    var b = intent.budget;
    var total;
    if (b && b.target != null) total = b.target;
    else if (b && b.max != null) total = b.max;
    else if (b && b.min != null) total = b.min;
    else total = cfg.defaultBudget;
    if (cfg.max) total = Math.min(total, cfg.max);
    return Math.max(total, cfg.min * 0.5);
  }

  /* Small rooms swap the biggest anchor for the smaller one. */
  function slotsFor(roomKey, intent) {
    var slots = ROOM_SLOTS[roomKey].slots.map(function (s) {
      return {
        key: s.key, name: s.name, share: s.share,
        required: !!s.required, lead: !!s.lead, types: s.types.slice()
      };
    });
    if (roomKey === 'living-room' && intent.scale === 'small') {
      slots[0].types = ['loveseats', 'sofas', 'chaise lounges', 'daybeds & futons'];
      slots[0].name = 'Anchor seating, scaled down';
    }
    if (roomKey === 'living-room' && intent.scale === 'large') {
      slots[0].types = ['sectional sofas', 'sofas', 'loveseats'];
    }
    return slots;
  }

  /* Builds the coordinated set.
   *
   *  1. The lead slot is filled first and sets the anchor style/palette.
   *  2. Remaining slots fill largest share first. Each slot's target is
   *     its share of the budget still unclaimed, so an underspend upstream
   *     becomes available downstream instead of being lost.
   *  3. Optional slots with nothing in range are dropped and reported,
   *     rather than padded with something that does not fit. */
  function buildSet(products, intent, roomKey) {
    var cfg = ROOM_SLOTS[roomKey];
    var slots = slotsFor(roomKey, intent);
    var total = budgetFor(intent, roomKey);

    var ordered = slots.slice().sort(function (a, b) {
      if (a.lead !== b.lead) return a.lead ? -1 : 1;
      return b.share - a.share;
    });

    var picks = [];
    var dropped = [];
    var spent = 0;
    var anchor = null;
    var used = {};

    ordered.forEach(function (slot) {
      var settled = {};
      picks.forEach(function (p) { settled[p.slot] = true; });
      var remainingShare = ordered.reduce(function (sum, s) {
        if (settled[s.key] || dropped.indexOf(s.name) !== -1) return sum;
        return sum + s.share;
      }, 0);
      var left = Math.max(total - spent, 0);
      var target = remainingShare > 0 ? left * (slot.share / remainingShare) : left;

      var sizeSlot = slot.key === 'bed' || slot.key === 'mattress' || slot.key === 'bedding';
      var slotIntent = {
        raw: intent.raw,
        styles: intent.styles, colors: intent.colors, materials: intent.materials,
        tone: intent.tone,
        bedSize: sizeSlot ? intent.bedSize : null,
        budget: { max: Math.max(target * 1.35, 25), min: target * 0.35, kind: 'slot' }
      };

      var passes = slotFilter(slot);
      function candidatesWith(intentObj) {
        var all = rank(products, intentObj, anchor, function (p) {
          if (used[p.handle]) return false;
          return passes(p);
        });
        /* Slot types are listed in priority order, and the first one is the
         * piece the slot is really about. Without this the "Rug & textiles"
         * slot bought a $29 pillow cover and the room had no rug in it. */
        var primary = all.filter(function (c) { return c.product.type === slot.types[0]; });
        return primary.length ? primary.concat(all.filter(function (c) {
          return c.product.type !== slot.types[0];
        })) : all;
      }

      var candidates = candidatesWith(slotIntent);

      /* Nothing in range: for a required slot, relax the cap to whatever
       * is left rather than abandoning the piece the room needs. The relaxed
       * flag is carried through to the card, because a pick that overshoots
       * its own allowance while being praised for "using the budget well"
       * reads as broken — the shopper should be told which rule bent. */
      var relaxed = false;
      if (!candidates.length && slot.required) {
        slotIntent.budget = { max: Math.max(left, 40), min: 0, kind: 'slot' };
        candidates = candidatesWith(slotIntent);
        relaxed = candidates.length > 0;
      }

      if (!candidates.length) {
        dropped.push(slot.name);
        return;
      }

      var top = candidates[0];
      used[top.product.handle] = true;
      spent += top.product.price;

      /* When the cap was relaxed, the budget clause scored against a cap the
       * shopper never set, so its wording is dropped rather than shown. */
      var reasons = relaxed
        ? top.reasons.filter(function (r) { return r.indexOf('budget') === -1 && r.indexOf('under') === -1; })
        : top.reasons;
      if (!reasons.length) reasons = ['$' + top.product.price.toFixed(0)];

      picks.push({
        slot: slot.key, slotName: slot.name, target: target, relaxed: relaxed,
        product: top.product, reasons: reasons, score: top.score,
        alternates: candidates.slice(1, 7).map(function (c) {
          return { product: c.product, reasons: c.reasons, score: c.score };
        })
      });

      if (slot.lead && !anchor) {
        anchor = {
          styles: top.product.styles.length ? top.product.styles : intent.styles,
          colors: top.product.colors.slice(0, 2),
          title: top.product.title
        };
      }
    });

    // Present in the room's natural reading order, not the fill order.
    var order = slots.map(function (s) { return s.key; });
    picks.sort(function (a, b) { return order.indexOf(a.slot) - order.indexOf(b.slot); });

    /* How much of the requested style the set actually delivers. With 774
     * of 1,773 products carrying a style tag, and some styles holding only
     * a couple of dozen pieces, a set often cannot be styled end to end.
     * That gets reported rather than glossed over. */
    var wanted = intent.styles[0] || (anchor && anchor.styles && anchor.styles[0]) || null;
    var onStyle = wanted ? picks.filter(function (p) {
      return p.product.styles.indexOf(wanted) !== -1;
    }).length : 0;

    return {
      room: roomKey, roomLabel: ROOMS[roomKey].label, note: cfg.note || null,
      budget: total, spent: spent, leftover: Math.max(total - spent, 0),
      picks: picks, dropped: dropped, anchor: anchor,
      style: wanted, styleLabel: wanted ? STYLES[wanted].label : null,
      onStyle: onStyle
    };
  }

  /* The palette strip is built from the colours of the pieces actually
   * picked, so it describes the real set rather than a generic swatch. */
  function paletteFor(picks, intent) {
    var counts = {};
    picks.forEach(function (p) {
      p.product.colors.forEach(function (c) { counts[c] = (counts[c] || 0) + 1; });
    });
    (intent.colors || []).forEach(function (c) {
      if (COLORS[c] && !COLORS[c].group) counts[c] = (counts[c] || 0) + 2;
    });
    var keys = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; });
    return keys.slice(0, 5).map(function (k) {
      return { key: k, label: COLORS[k].label, hex: COLORS[k].hex };
    });
  }

  /* Novelty and seasonal pieces are never right on a style board, whatever
   * their style tag says. A red-hearts throw pillow carrying a Minimalist tag
   * was offered as a neighbour of Japandi; the tag is wrong, but a board
   * should not depend on every tag being right. */
  var BOARD_EXCLUDE = ['heart', 'valentine', 'christmas', 'xmas', 'halloween',
    'easter', 'thanksgiving', 'gnome', 'santa', 'pumpkin', 'motivational',
    'cartoon', 'anime', 'birthday',
    /* Loud pattern language undercuts a board whatever the style tag says.
     * Removing the red hearts pillow promoted a "Colorful Flowers" one into
     * the same slot, which is the same defect wearing different print. */
    'colorful', 'floral', 'flowers', 'rainbow', 'multicolor', 'multi-color',
    'neon', 'psychedelic', 'tie dye', 'tie-dye', 'leopard', 'zebra'];

  /* A style board when only a look was named. */
  function buildLook(products, intent) {
    var used = {};
    var lines = [];
    var passOver = [];
    LOOK_LINES.forEach(function (line) {
      var passes = slotFilter(line);
      var candidates = rank(products, intent, null, function (p) {
        if (used[p.handle]) return false;
        return passes(p);
      });
      /* A style board that pads a thin row with unstyled filler stops being
       * a style board. When a look was asked for, only pieces carrying that
       * look or a neighbouring one are eligible, and a short row is the
       * honest outcome. */
      candidates = candidates.filter(function (c) {
        return !BOARD_EXCLUDE.some(function (w) { return c.product.titleLower.indexOf(w) !== -1; });
      });
      if (intent.styles.length) {
        var near = [intent.styles[0]].concat(STYLE_ADJACENT[intent.styles[0]] || []);
        candidates = candidates.filter(function (c) {
          return c.product.styles.some(function (s) { return near.indexOf(s) !== -1; });
        });
      }
      if (!candidates.length) { passOver.push(line.name); return; }
      /* One piece per type and per brand, so a row reads as a row of
         different things rather than three of the same thing. */
      var chosen = dedupeVariety(candidates, 3, 1, 1);
      // A board reads better anchored by the larger piece.
      chosen.sort(function (a, b) { return b.product.price - a.product.price; });
      chosen.forEach(function (c) { used[c.product.handle] = true; });
      var onStyle = intent.styles.length ? chosen.filter(function (c) {
        return c.product.styles.indexOf(intent.styles[0]) !== -1;
      }).length : 0;
      lines.push({ name: line.name, items: chosen, onStyle: onStyle });
    });
    return { lines: lines, skipped: passOver };
  }

  function buildGift(products, intent) {
    /* Without a stated budget, gift results sorted by price put an $800
     * designer kettle at the top of "a gift for my wife who likes coffee".
     * A default gifting band is a better assumption than no band at all,
     * and the UI says which band it used. */
    var outOfReach = null;
    if (!intent.budget) {
      intent = Object.keys(intent).reduce(function (o, k) { o[k] = intent[k]; return o; }, {});
      intent.budget = { max: 150, min: 20, kind: 'gift-default' };
    }

    if (intent.categories.length) {
      // A named piece is a stronger signal than an inferred interest.
      var types = [];
      intent.categories.forEach(function (c) {
        (CATEGORIES[c].types || []).forEach(function (t) { types.push(t); });
      });
      var direct = rank(products, intent, null, function (p) { return typeMatches(p, types); });
      if (direct.length) {
        return [{
          key: 'direct',
          label: intent.categories.map(function (c) { return CATEGORIES[c].label; }).join(' & '),
          items: dedupeVariety(direct, 8, 3, 2)
        }];
      }
      /* The shopper named a piece and the budget rules all of it out. Saying
       * so, with the real entry price, beats quietly showing dinnerware to
       * someone who asked about grills. */
      var cheapest = null;
      products.forEach(function (p) {
        if (!typeMatches(p, types)) return;
        if (!cheapest || p.price < cheapest.price) cheapest = p;
      });
      if (cheapest) {
        /* Show the real pieces just past the cap. Someone shopping for a
         * grilling dad is better served by "grills start at $144" and four
         * actual grills than by a $27 wall planter that cleared the budget. */
        var overBudget = Object.keys(intent).reduce(function (o, k) { o[k] = intent[k]; return o; }, {});
        overBudget.budget = null;
        outOfReach = {
          label: intent.categories.map(function (c) { return CATEGORIES[c].label; }).join(' & '),
          from: cheapest.price,
          cap: intent.budget.max,
          nearest: dedupeVariety(
            rank(products, overBudget, null, function (p) { return typeMatches(p, types); })
              .sort(function (a, b) { return a.product.price - b.product.price; }),
            4, 0, 2)
        };
      }
    }
    var lanes = intent.lanes.slice();

    /* If a named piece was out of reach, lead with the lane that piece
     * belongs to. "Dad who grills, under $100" has no grills under $100,
     * but the outdoor lane holds grill tools, thermometers and fire pits,
     * which is much closer to the request than default dinnerware. */
    if (outOfReach && intent.categories.length) {
      var wantTypes = [];
      intent.categories.forEach(function (c) {
        (CATEGORIES[c].types || []).forEach(function (t) { wantTypes.push(t); });
      });
      Object.keys(GIFT_LANES).forEach(function (k) {
        if (lanes.indexOf(k) !== -1) return;
        if (GIFT_LANES[k].types.some(function (t) { return wantTypes.indexOf(t) !== -1; })) lanes.unshift(k);
      });
    }

    if (!lanes.length) lanes = ['host', 'cozy', 'cooking', 'decor'];
    var used = {};
    var out = [];
    lanes.forEach(function (k) {
      var lane = GIFT_LANES[k];
      if (!lane) return;
      var candidates = rank(products, intent, null, function (p) {
        if (used[p.handle]) return false;
        return typeMatches(p, lane.types) && passesTitleFilters(p, lane);
      });
      if (!candidates.length) return;
      var items = dedupeVariety(candidates, 6, 2, 2);
      items.forEach(function (c) { used[c.product.handle] = true; });
      out.push({ key: k, label: 'For ' + lane.label, items: items });
    });
    /* With a "nothing under your cap" block already carrying the answer, a
     * one-item consolation lane adds noise rather than help. */
    if (outOfReach && outOfReach.nearest && outOfReach.nearest.length) {
      out = out.filter(function (g) { return g.items.length >= 2; });
    }

    if (outOfReach) out.outOfReach = outOfReach;
    out.budgetAssumed = intent.budget.kind === 'gift-default' ? intent.budget.max : null;
    return out;
  }

  function buildItem(products, intent) {
    var used = {};
    return intent.categories.map(function (catKey) {
      var candidates = rank(products, intent, null, function (p) {
        if (used[p.handle]) return false;
        return categoryMatches(p, catKey);
      });
      /* A named finish, colour or material is a request. Mixing pieces that
         miss it into the same grid made five of eight results for "walnut
         coffee table" not walnut, with no way to tell which was which. They
         are split instead, and the second grid says what it is missing. */
      var must = null, mustLabel = null;
      if (intent.materials.length) {
        must = function (p) { return p.materials.indexOf(intent.materials[0]) !== -1; };
        mustLabel = MATERIALS[intent.materials[0]].label.toLowerCase();
      } else if (intent.colors.length) {
        must = function (p) { return colorSatisfied(p, intent.colors[0]); };
        mustLabel = COLORS[intent.colors[0]].label.toLowerCase();
      }

      var hits = must ? candidates.filter(function (c) { return must(c.product); }) : candidates;
      var misses = must ? candidates.filter(function (c) { return !must(c.product); }) : [];

      /* Three per brand keeps a single seller from owning the grid while
         still letting a deep, well-matched range show through. */
      var items = dedupeVariety(hits, 12, 0, 3);
      var others = misses.length ? dedupeVariety(misses, Math.max(4, 8 - items.length), 0, 2) : [];
      items.concat(others).forEach(function (c) { used[c.product.handle] = true; });

      var onStyle = intent.styles.length ? items.filter(function (c) {
        return c.product.styles.indexOf(intent.styles[0]) !== -1;
      }).length : 0;
      return {
        key: catKey,
        label: CATEGORIES[catKey].label,
        items: items,
        others: others,
        missingLabel: mustLabel,
        totalFound: candidates.length,
        onStyle: onStyle
      };
    });
  }

  /* One entry point for every mode. Resolves the pool, then dispatches. */
  function run(intent) {
    return loadIndex().then(function (idx) {
      var products = idx.products;
      var result = {
        intent: intent, mode: intent.mode,
        poolSize: products.length, generated: idx.generated
      };
      if (intent.mode === 'room') {
        result.set = buildSet(products, intent, intent.room);
        result.palette = paletteFor(result.set.picks, intent);
      } else if (intent.mode === 'item') {
        result.groups = buildItem(products, intent);
        result.palette = paletteFor(
          result.groups.reduce(function (a, g) { return a.concat(g.items.slice(0, 4)); }, []),
          intent
        );
      } else if (intent.mode === 'gift') {
        result.groups = buildGift(products, intent);
      } else if (intent.mode === 'look') {
        var look = buildLook(products, intent);
        result.lines = look.lines;
        result.skippedLines = look.skipped;
        result.palette = paletteFor(
          look.lines.reduce(function (a, l) { return a.concat(l.items); }, []),
          intent
        );
      }
      return result;
    });
  }
  /* ===================================================================
   * 6. INTERFACE
   * One text field. What the parser understood is shown back as chips the
   * shopper can remove, so a wrong reading is a click to fix rather than a
   * dead end. Everything below no-ops when the section is not on the page.
   * ================================================================ */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function money(n) {
    var v = Number(n) || 0;
    return '$' + v.toFixed(v < 100 ? 2 : 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  var EXAMPLES = [
    'Furnish my living room for $2,000 in mid-century modern',
    'Small living room under $1,200 in warm neutrals',
    'Bedroom for $1,800, queen, japandi',
    'Set up my home office for $900',
    'Walnut coffee table under $300',
    'Housewarming gift under $50',
    'Japandi look',
    'Spa-like bathroom in neutrals'
  ];

  function ModernMatch(root) {
    var state = { intent: null, result: null, busy: false, swaps: {} };

    var form   = root.querySelector('[data-mm-form]');
    var input  = root.querySelector('[data-mm-input]');
    var chips  = root.querySelector('[data-mm-examples]');
    var heard  = root.querySelector('[data-mm-heard]');
    var out    = root.querySelector('[data-mm-results]');
    if (!form || !input || !out) return;

    INDEX_URL = root.getAttribute('data-index-url') || INDEX_URL;

    // ---- examples ----------------------------------------------------
    if (chips) {
      chips.innerHTML = EXAMPLES.map(function (q) {
        return '<button type="button" class="gift-chip" data-mm-example="' + esc(q) + '">' + esc(q) + '</button>';
      }).join('');
    }

    root.addEventListener('click', function (e) {
      var ex = e.target.closest('[data-mm-example]');
      if (ex) {
        input.value = ex.getAttribute('data-mm-example');
        submit();
        return;
      }

      /* The heart is deliberately left alone. wishlist.js listens on document
       * and already calls preventDefault, so the anchor does not navigate;
       * calling stopPropagation here would be the bug, not the fix. The Gift
       * Finder's own heart is broken for exactly that reason — its inline
       * onclick stops propagation, so the wishlist listener never runs. */
      if (e.target.closest('[data-wishlist-toggle]')) return;

      var drop = e.target.closest('[data-mm-drop]');
      if (drop) {
        e.preventDefault();
        dropFacet(drop.getAttribute('data-mm-drop'), drop.getAttribute('data-mm-drop-value'));
        return;
      }

      var swap = e.target.closest('[data-mm-swap]');
      if (swap) {
        e.preventDefault();
        swapPick(swap.getAttribute('data-mm-swap'), parseInt(swap.getAttribute('data-mm-swap-to'), 10));
      }
    });

    form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });

    function submit() {
      var q = (input.value || '').trim();
      if (!q || state.busy) return;
      state.intent = parseIntent(q);
      state.swaps = {};
      render();
      go();
    }

    function go() {
      state.busy = true;
      out.innerHTML = '<div class="budget-loading">Matching against the catalog…</div>';
      run(state.intent).then(function (result) {
        state.busy = false;
        state.result = result;
        render();
        refreshPrices();
      }).catch(function (err) {
        state.busy = false;
        out.innerHTML = '<div class="mm-empty"><p>Something went wrong loading the catalog. ' +
          'Please try again in a moment.</p></div>';
        if (window.console) console.error('[Modern Match]', err);
      });
    }

    /* Removing a chip re-runs the search without that facet, which is the
     * fastest way out of a misread query. */
    function dropFacet(kind, value) {
      var i = state.intent;
      if (!i) return;
      if (kind === 'room') { i.room = null; }
      else if (kind === 'budget') { i.budget = null; }
      else if (kind === 'bedSize') { i.bedSize = null; }
      else if (kind === 'tone') { i.tone = null; }
      else if (kind === 'scale') { i.scale = null; }
      else if (kind === 'whole') { i.whole = false; }
      else if (i[kind] && i[kind].indexOf) {
        i[kind] = i[kind].filter(function (v) { return v !== value; });
      }
      i.mode = inferMode(i);
      state.swaps = {};
      render();
      if (i.mode) go(); else out.innerHTML = emptyState();
    }

    function swapPick(slotKey, altIndex) {
      state.swaps[slotKey] = altIndex;
      render();
    }

    /* Index prices are a snapshot. Only what is on screen gets re-checked,
     * which is a couple of dozen small requests rather than the catalog. */
    function refreshPrices() {
      var nodes = out.querySelectorAll('[data-mm-handle]');
      var handles = [];
      for (var i = 0; i < nodes.length && handles.length < 30; i++) {
        var h = nodes[i].getAttribute('data-mm-handle');
        if (handles.indexOf(h) === -1) handles.push(h);
      }
      handles.forEach(function (h) {
        fetch('/products/' + h + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            if (!p) return;
            var live = (p.price != null ? p.price / 100 : null);
            if (live == null) return;
            var cards = out.querySelectorAll('[data-mm-handle="' + h + '"]');
            for (var j = 0; j < cards.length; j++) {
              var el = cards[j].querySelector('[data-mm-price]');
              if (!el) continue;
              var shown = parseFloat(el.getAttribute('data-mm-price'));
              if (Math.abs(shown - live) >= 0.5) {
                el.textContent = money(live);
                el.setAttribute('data-mm-price', live);
                el.classList.add('mm-price--updated');
              }
            }
          })
          .catch(function () { /* a stale price is not worth an error state */ });
      });
    }

    // ---- chips for what was understood --------------------------------
    function heardChips() {
      var i = state.intent;
      if (!i) return '';
      var items = [];
      function chip(kind, value, label) {
        items.push('<button type="button" class="mm-heard__chip" data-mm-drop="' + kind + '" ' +
          'data-mm-drop-value="' + esc(value) + '" title="Remove">' +
          esc(label) + '<span aria-hidden="true">×</span></button>');
      }
      if (i.room) chip('room', i.room, ROOMS[i.room].label);
      i.categories.forEach(function (c) { chip('categories', c, CATEGORIES[c].label); });
      i.styles.forEach(function (s) { chip('styles', s, STYLES[s].label); });
      i.colors.forEach(function (c) { chip('colors', c, COLORS[c].label); });
      i.materials.forEach(function (m) { chip('materials', m, MATERIALS[m].label); });
      if (i.bedSize) chip('bedSize', i.bedSize, i.bedSize);
      if (i.tone) chip('tone', i.tone, i.tone.charAt(0).toUpperCase() + i.tone.slice(1));
      if (i.scale) chip('scale', i.scale, i.scale.charAt(0).toUpperCase() + i.scale.slice(1) + ' space');
      if (i.budget) {
        var b = i.budget, label;
        if (b.kind === 'under') label = 'Under ' + money(b.max);
        else if (b.kind === 'range') label = money(b.min) + '–' + money(b.max);
        else label = 'Around ' + money(b.target != null ? b.target : b.max);
        chip('budget', 'budget', label);
      }
      if (!items.length) return '';
      return '<div class="mm-heard"><span class="mm-heard__label">Matching on</span>' +
        items.join('') + '</div>';
    }

    function paletteStrip(palette) {
      if (!palette || !palette.length) return '';
      return '<div class="mm-palette">' +
        '<span class="mm-palette__label">Palette in this set</span>' +
        '<div class="mm-palette__row">' +
        palette.map(function (c) {
          return '<span class="mm-palette__swatch" title="' + esc(c.label) + ' ' + c.hex + '">' +
            '<i style="background:' + c.hex + '"></i>' + esc(c.label) + '</span>';
        }).join('') + '</div></div>';
    }

    function card(scored, opts) {
      opts = opts || {};
      var p = scored.product;
      var t = esc(p.title);
      var img = p.image ? esc(p.image) : '';
      return '<div class="gift-result-card mm-card" data-mm-handle="' + esc(p.handle) + '">' +
        '<a class="mm-card__link" href="' + esc(p.url) + '">' +
          '<div class="gift-result-card__image-wrap">' +
            '<button type="button" class="wishlist-heart wishlist-heart--overlay" data-wishlist-toggle ' +
              'data-wishlist-id="' + esc(p.handle) + '" data-wishlist-title="' + t + '" ' +
              'data-wishlist-url="' + esc(p.url) + '" data-wishlist-image="' + img + '" ' +
              'data-wishlist-price="' + p.price + '" aria-label="Save to wishlist">' +
              '<svg viewBox="0 0 24 24"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>' +
            '</button>' +
            (img ? '<img src="' + img + '" loading="lazy" alt="' + t + '">' : '') +
          '</div>' +
          '<div class="gift-result-card__title">' + t + '</div>' +
          '<div class="gift-result-card__price" data-mm-price="' + p.price + '">' + money(p.price) + '</div>' +
        '</a>' +
        (scored.reasons && scored.reasons.length
          ? '<ul class="mm-card__why">' + scored.reasons.slice(0, 3).map(function (r) {
              return '<li>' + esc(r) + '</li>';
            }).join('') + '</ul>'
          : '') +
        (opts.footer || '') +
        '</div>';
    }

    function renderSet(result) {
      var set = result.set;
      var html = '';

      html += '<div class="mm-summary">' +
        '<div class="mm-summary__head">' +
          '<h3>' + esc(set.roomLabel) + (set.styleLabel ? ' · ' + esc(set.styleLabel) : '') + '</h3>' +
          '<div class="mm-summary__spend"><strong>' + money(set.spent) + '</strong>' +
            '<span>of ' + money(set.budget) + '</span></div>' +
        '</div>' +
        '<div class="mm-bar"><i style="width:' +
          Math.min(100, Math.round(set.spent / set.budget * 100)) + '%"></i></div>' +
        '<p class="mm-summary__line">' + set.picks.length + ' pieces chosen together' +
          (set.leftover > set.budget * 0.08
            ? ', leaving ' + money(set.leftover) + ' unspent'
            : '') + '.' +
          (set.anchor
            ? ' Everything after the ' + esc(set.picks[0] ? set.picks[0].slotName.toLowerCase() : 'lead piece') +
              ' was matched to it.'
            : '') +
        '</p>';

      /* Style coverage is stated plainly. The catalog carries a style tag on
       * 774 of 1,773 pieces, and some looks hold only a couple of dozen, so
       * claiming a fully styled set would often be false. */
      if (set.styleLabel) {
        html += '<p class="mm-note">' + set.onStyle + ' of ' + set.picks.length +
          ' pieces are tagged ' + esc(set.styleLabel) +
          (set.onStyle < set.picks.length
            ? '. The rest were chosen from neighbouring looks that sit well with it.'
            : '.') + '</p>';
      }
      if (set.note) html += '<p class="mm-note">' + esc(set.note) + '</p>';
      if (set.dropped.length) {
        html += '<p class="mm-note mm-note--warn">Nothing in range for: ' +
          esc(set.dropped.join(', ')) + '. Raising the budget or dropping a filter usually fixes it.</p>';
      }
      html += '</div>';

      html += paletteStrip(result.palette);

      html += '<div class="mm-slots">';
      set.picks.forEach(function (pick) {
        var idx = state.swaps[pick.slot];
        var chosen = (idx != null && pick.alternates[idx]) ? pick.alternates[idx] : pick;
        var footer = '';
        if (pick.relaxed) {
          footer += '<p class="mm-slot__relaxed">Nothing suitable fitted the ' +
            money(pick.target) + ' allowance, so this slot was allowed to go over. ' +
            'The room needs this piece more than the split needs protecting.</p>';
        }
        if (pick.alternates.length) {
          footer += '<div class="mm-alts"><span>Or</span>' +
            (idx != null
              ? '<button type="button" class="mm-alts__btn is-active" data-mm-swap="' + esc(pick.slot) +
                '" data-mm-swap-to="">the original</button>'
              : '') +
            pick.alternates.slice(0, 4).map(function (a, n) {
              return '<button type="button" class="mm-alts__btn' + (idx === n ? ' is-active' : '') +
                '" data-mm-swap="' + esc(pick.slot) + '" data-mm-swap-to="' + n + '">' +
                money(a.product.price) + '</button>';
            }).join('') + '</div>';
        }
        html += '<div class="mm-slot">' +
          '<div class="mm-slot__head">' +
            '<h4>' + esc(pick.slotName) + '</h4>' +
            '<span class="mm-slot__target' + (pick.relaxed ? ' is-over' : '') + '">allowance ' +
              money(pick.target) + '</span>' +
          '</div>' +
          card(chosen, { footer: footer }) +
          '</div>';
      });
      html += '</div>';

      /* A set implies a floor plan question, and the Space Planner already
       * answers it, so the handoff is offered rather than rebuilt here. */
      if (state.intent.dims && state.intent.dims.kind === 'room') {
        html += '<p class="mm-handoff">Working with a ' + esc(state.intent.dims.raw) +
          ' room? <a href="/pages/space-planner">Check the fit in the Space Planner</a>.</p>';
      }
      return html;
    }

    function renderGroups(result) {
      var groups = result.groups || [];
      var html = '';
      if (groups.budgetAssumed) {
        html += '<p class="mm-note">No budget mentioned, so these are under ' +
          money(groups.budgetAssumed) + '. Say a number to change that.</p>';
      }
      if (groups.outOfReach) {
        var o = groups.outOfReach;
        html += '<div class="mm-summary mm-summary--warn">' +
          '<h3>Nothing in ' + esc(o.label.toLowerCase()) + ' under ' + money(o.cap) + '</h3>' +
          '<p class="mm-summary__line">The ones carried here start at ' + money(o.from) +
          '. Closest options:</p></div>' +
          '<div class="gift-results-grid">' +
          o.nearest.map(function (c) { return card(c); }).join('') + '</div>';
      }
      groups.forEach(function (g) {
        html += '<div class="mm-group"><div class="mm-group__head"><h3>' + esc(g.label) + '</h3>' +
          (g.totalFound
            ? '<span>' + g.items.length + ' of ' + g.totalFound + ' in the catalog</span>'
            : '') + '</div>' +
          '<div class="gift-results-grid">' +
          g.items.map(function (c) { return card(c); }).join('') + '</div>';

        /* A named finish or colour is a request, not a hint. Pieces that miss
         * it are still worth showing, but under their own heading rather than
         * mixed in as though they matched. */
        if (g.others && g.others.length) {
          html += '<p class="mm-group__aside">Also close, without the ' +
            esc(g.missingLabel) + ':</p>' +
            '<div class="gift-results-grid">' +
            g.others.map(function (c) { return card(c); }).join('') + '</div>';
        }
        html += '</div>';
      });
      if (!groups.length && !groups.outOfReach) html += emptyResults();
      return html;
    }

    function renderLook(result) {
      var html = '';
      var styleLabel = state.intent.styles.length ? STYLES[state.intent.styles[0]].label : 'This look';
      html += '<div class="mm-summary"><div class="mm-summary__head"><h3>' + esc(styleLabel) + '</h3></div>' +
        '<p class="mm-summary__line">Pieces carrying this look, or a neighbouring one, across the store.</p>';
      if (result.skippedLines && result.skippedLines.length) {
        html += '<p class="mm-note">Nothing tagged for: ' + esc(result.skippedLines.join(', ')) +
          '. Those rows are left out rather than filled with pieces that do not fit.</p>';
      }
      html += '</div>' + paletteStrip(result.palette);
      (result.lines || []).forEach(function (line) {
        html += '<div class="mm-group"><div class="mm-group__head"><h3>' + esc(line.name) + '</h3></div>' +
          '<div class="gift-results-grid">' +
          line.items.map(function (c) { return card(c); }).join('') + '</div></div>';
      });
      if (!(result.lines || []).length) html += emptyResults();
      return html;
    }

    function emptyResults() {
      return '<div class="mm-empty"><p>Nothing matched that closely enough to recommend. ' +
        'Removing a chip above usually opens it up.</p></div>';
    }

    function emptyState() {
      return '<div class="mm-empty">' +
        '<p>Tell me the room, the piece, the look, or who you are buying a gift for — '+
        'a budget helps too.</p>' +
        '<p class="mm-empty__small">Try one of the examples above.</p></div>';
    }

    function render() {
      if (heard) heard.innerHTML = heardChips();
      if (state.busy) return;
      if (!state.intent) { out.innerHTML = emptyState(); return; }
      if (!state.intent.mode) { out.innerHTML = emptyState(); return; }
      if (!state.result) return;

      var r = state.result;
      var html = '';
      if (r.mode === 'room' && r.set) html = renderSet(r);
      else if (r.mode === 'look') html = renderLook(r);
      else html = renderGroups(r);

      if (r.generated) {
        html += '<p class="mm-stamp">Catalog snapshot ' + esc(r.generated) +
          '. Prices on screen are re-checked live; always confirm on the product page before buying.</p>';
      }
      out.innerHTML = html;
      if (window.MSGWishlist) window.MSGWishlist.refresh();
    }

    render();
  }

  function init() {
    var nodes = document.querySelectorAll('[data-modern-match]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].__mmReady) {
        nodes[i].__mmReady = true;
        ModernMatch(nodes[i]);
      }
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
    document.addEventListener('shopify:section:load', init);
  }
  /* All pure logic is exported so it can be exercised in Node without a
   * DOM. The DOM layer below is skipped entirely when the section is not
   * on the page. */
  var MSGMatch = {
    ROOMS: ROOMS, CATEGORIES: CATEGORIES, STYLES: STYLES, COLORS: COLORS,
    MATERIALS: MATERIALS, ROOM_SLOTS: ROOM_SLOTS, LOOK_LINES: LOOK_LINES,
    GIFT_LANES: GIFT_LANES, STYLE_ADJACENT: STYLE_ADJACENT,
    BED_SIZES: BED_SIZES, TONE_WORDS: TONE_WORDS,
    normalize: normalize, parseIntent: parseIntent,
    reduceProduct: reduceProduct, rowToProduct: rowToProduct, productToRow: productToRow,
    detectAll: detectAll, detectSizes: detectSizes, setIndexUrl: function (u) { INDEX_URL = u; },
    scoreProduct: scoreProduct, rank: rank, categoryMatches: categoryMatches,
    typeMatches: typeMatches, buildSet: buildSet, buildLook: buildLook,
    buildGift: buildGift, buildItem: buildItem, paletteFor: paletteFor,
    budgetFor: budgetFor, run: run
  };
  if (typeof window !== 'undefined') window.MSGMatch = MSGMatch;
  if (typeof module !== 'undefined' && module.exports) module.exports = MSGMatch;
})();
